import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import {
  FaNetworkWired, FaEdit, FaPlus, FaTrash, FaSync, FaCheck, FaTimes,
  FaSpinner, FaToggleOn, FaToggleOff, FaGlobe,
  FaSitemap, FaEthernet, FaLink, FaUnlink,
  FaExclamationTriangle, FaCheckCircle, FaChevronRight, FaChevronDown,
  FaSignal, FaScroll, FaTerminal, FaHistory, FaExpand, FaCompress,
  FaPaperPlane, FaFilter, FaServer, FaRoute, FaDna,
  FaBook, FaSearch, FaInfoCircle,
  FaWifi, FaBroadcastTower,
  FaLock, FaExchangeAlt, FaThumbsUp, FaTable,
  FaEye, FaPen, FaBold, FaItalic, FaListUl, FaListOl, FaCode, FaQuoteRight,
  FaHeading, FaImage, FaStickyNote, FaMinus,
  FaFileContract, FaSave, FaBuilding, FaCalendarAlt, FaFileUpload,
  FaDownload, FaFilePdf, FaShieldAlt, FaTachometerAlt,
  FaProjectDiagram
} from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PdfAnnotationModal from '@/components/PdfAnnotationModal';
import InterfaceDetailModal from './InterfaceDetailModal';
import './RouterInterfaceManagement.scss';
import * as Sentry from '@sentry/react';

// =====================================================================
// Shared helpers
// =====================================================================
const useApiCall = () => {
  const { apiFetch } = useAuth();
  return useCallback(async (url, opts = {}) => {
    const response = await apiFetch(url, {
      headers: { 'Content-Type': 'application/json', ...opts.headers },
      ...opts
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return data;
  }, [apiFetch]);
};

// =====================================================================
// Helper: builds a human-readable option label for an interface,
// including a Unicode status indicator so users can see whether a
// port is running, disabled, or down before assigning an IP address.
//   ● Running  — the interface is up and carrying traffic
//   ○ Down     — the interface is enabled but not currently up
//   ⊘ Disabled — the interface has been administratively disabled
// =====================================================================
const getInterfaceStatusLabel = (iface) => {
  if (iface.disabled) return `⊘ ${iface.name} (${iface.type}) — Disabled`;
  if (iface.running) return `● ${iface.name} (${iface.type}) — Running`;
  return `○ ${iface.name} (${iface.type}) — Down`;
};

// =====================================================================
// InterfaceStatusSelect — custom select wrapper that augments the
// standard <select> with a colored status chip showing the health of
// the currently selected port. Because <option> elements cannot be
// individually colored cross-browser, the chip is a separate element
// that updates reactively when the user changes their selection.
//
//   ● Running  — green chip   (interface is up and passing traffic)
//   ● Down     — amber chip   (interface is enabled but not currently up)
//   ● Disabled — gray chip    (interface has been administratively disabled)
// =====================================================================
const InterfaceStatusSelect = ({
  value,
  onChange,
  interfaces,
  placeholder = 'Select interface...',
  className = ''
}) => {
  /**
   * Returns display configuration for the given interface's status.
   * Returns null when no interface is selected.
   */
  const getStatusConfig = (iface) => {
    if (!iface) return null;
    if (iface.disabled) return { label: 'Disabled', cssClass: 'isc-disabled' };
    if (iface.running)  return { label: 'Running',  cssClass: 'isc-running'  };
    return                     { label: 'Down',     cssClass: 'isc-down'     };
  };

  const selectedInterface = interfaces.find(i => i.name === value);
  const statusConfig = getStatusConfig(selectedInterface);

  return (
    <div className={`ifc-status-select-wrap ${className}`}>
      <select value={value} onChange={onChange}>
        <option value="">{placeholder}</option>
        {interfaces.map(i => (
          <option key={i.name} value={i.name}>
            {getInterfaceStatusLabel(i)}
          </option>
        ))}
      </select>
      {statusConfig && (
        <span
          className={`ifc-status-chip ${statusConfig.cssClass}`}
          title={`Port status: ${statusConfig.label}`}
          aria-label={`Port status: ${statusConfig.label}`}
        >
          <span className="ifc-status-dot" aria-hidden="true" />
          {statusConfig.label}
        </span>
      )}
    </div>
  );
};

// =====================================================================
// =====================================================================
/**
 * Modal dialog for managing the internet service contract associated with
 * a single RouterPublicAddress entry.
 *
 * Responsibilities:
 *  - Load the existing contract (if any) from GET /api/ServiceProviders/contracts/by-address/{id}
 *  - Load the list of service providers from GET /api/ServiceProviders?activeOnly=true
 *  - Allow editing all contract fields (ISP, throughput, SLA, expiry, reminder days)
 *  - Save via POST /api/ServiceProviders/contracts  (upsert)
 *  - List, upload, download, and soft-delete contract documents
 *
 * Exported for reuse in RouterDetailPage (Intelligence → ISP Links tab).
 */
export const IspContractModal = ({ publicAddress, onClose }) => {
  const { apiFetch, user } = useAuth();

  const [contract, setContract] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef(null);

  // PDF annotation state
  const [annotatingDoc, setAnnotatingDoc] = useState(null);

  // Contract history state
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Local editable form state — mirrors InternetServiceContract fields
  const [form, setForm] = useState({
    serviceProviderId: '',
    expectedDownloadMbps: '',
    expectedUploadMbps: '',
    uptimeSlaPercent: '',
    contractExpiryDate: '',
    contractExpiryReminderDays: '',
    notes: ''
  });

  // ── Load contract + providers on mount ──────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [contractRes, providersRes] = await Promise.all([
          apiFetch(`/api/ServiceProviders/contracts/by-address/${publicAddress.id}`),
          apiFetch('/api/ServiceProviders?activeOnly=true')
        ]);

        if (contractRes.ok) {
          const contractJson = await contractRes.json();
          if (contractJson.success && contractJson.data) {
            const c = contractJson.data;
            setContract(c);
            setForm({
              serviceProviderId: c.serviceProviderId ?? '',
              expectedDownloadMbps: c.expectedDownloadMbps ?? '',
              expectedUploadMbps: c.expectedUploadMbps ?? '',
              uptimeSlaPercent: c.uptimeSlaPercent ?? '',
              contractExpiryDate: c.contractExpiryDate
                ? new Date(c.contractExpiryDate).toISOString().split('T')[0]
                : '',
              contractExpiryReminderDays: c.contractExpiryReminderDays ?? '',
              notes: c.notes ?? ''
            });
          }
        }

        if (providersRes.ok) {
          const providersJson = await providersRes.json();
          if (providersJson.success) {
            setProviders(providersJson.data || []);
          }
        }
      } catch (err) {
          Sentry.captureException(err);
        console.error('Failed to load ISP contract:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [publicAddress.id, apiFetch]);

  // ── Load history when contract is available ──────────────────────────────────
  useEffect(() => {
    if (!contract?.internetServiceContractId) return;
    (async () => {
      setLoadingHistory(true);
      try {
        const r = await apiFetch(`/api/ServiceProviders/contracts/${contract.internetServiceContractId}/history`);
        const data = await r.json();
        setHistory(data.data || []);
      } catch {
        // ignore
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [contract?.internetServiceContractId, apiFetch]);

  // ── Save contract ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        routerPublicAddressId: publicAddress.id,
        serviceProviderId: form.serviceProviderId !== '' ? parseInt(form.serviceProviderId) : null,
        expectedDownloadMbps: form.expectedDownloadMbps !== '' ? parseFloat(form.expectedDownloadMbps) : null,
        expectedUploadMbps: form.expectedUploadMbps !== '' ? parseFloat(form.expectedUploadMbps) : null,
        uptimeSlaPercent: form.uptimeSlaPercent !== '' ? parseFloat(form.uptimeSlaPercent) : null,
        contractExpiryDate: form.contractExpiryDate || null,
        contractExpiryReminderDays: form.contractExpiryReminderDays !== '' ? parseInt(form.contractExpiryReminderDays) : null,
        notes: form.notes.trim() || null
      };

      const res = await apiFetch('/api/ServiceProviders/contracts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();

      if (json.success) {
        // Reload contract to get the persisted ID and document list
        const reloadRes = await apiFetch(`/api/ServiceProviders/contracts/by-address/${publicAddress.id}`);
        if (reloadRes.ok) {
          const reloadJson = await reloadRes.json();
          if (reloadJson.success) setContract(reloadJson.data);
        }
        toast.success('Contract saved successfully.');
      } else {
        toast.error(json.error || 'Failed to save contract.');
      }
    } catch (err) {
        Sentry.captureException(err);
      toast.error('Error saving contract.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ── Document upload ──────────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !contract?.internetServiceContractId) return;

    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('File', file);
      fd.append('UserId', user?.userID ?? user?.UserID ?? 0);

      const res = await apiFetch(
        `/api/ServiceProviders/contracts/${contract.internetServiceContractId}/documents`,
        { method: 'POST', body: fd }
      );
      const json = await res.json();

      if (json.success) {
        // Refresh contract to show new document
        const reloadRes = await apiFetch(`/api/ServiceProviders/contracts/by-address/${publicAddress.id}`);
        if (reloadRes.ok) {
          const reloadJson = await reloadRes.json();
          if (reloadJson.success) setContract(reloadJson.data);
        }
        toast.success('Document uploaded successfully.');
      } else {
        toast.error(json.error || 'Upload failed.');
      }
    } catch (err) {
        Sentry.captureException(err);
      toast.error('Upload error.');
      console.error(err);
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Document delete ──────────────────────────────────────────────────────────
  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const res = await apiFetch(`/api/ServiceProviders/documents/${docId}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) {
        setContract(prev => ({
          ...prev,
          documents: prev.documents.filter(d => d.serviceProviderDocumentId !== docId)
        }));
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Delete error:', err);
    }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // ── Expiry helpers ────────────────────────────────────────────────────────────
  const daysUntilExpiry = form.contractExpiryDate
    ? Math.ceil((new Date(form.contractExpiryDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const expiryClass = daysUntilExpiry === null ? ''
    : daysUntilExpiry <= 7 ? 'isp-expiry-critical'
    : daysUntilExpiry <= 30 ? 'isp-expiry-warning'
    : 'isp-expiry-ok';

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="isp-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="isp-modal">
        {/* Header */}
        <div className="isp-modal-header">
          <div className="isp-modal-title">
            <FaFileContract size={16} />
            <div>
              <span className="isp-modal-label">Service Contract</span>
              <span className="isp-modal-ip">{publicAddress.label || publicAddress.ipAddress}</span>
            </div>
          </div>
          <button className="isp-modal-close" onClick={onClose} title="Close"><FaTimes /></button>
        </div>

        {/* Body */}
        <div className="isp-modal-body">
          {loading ? (
            <div className="isp-loading"><FaSpinner className="spin" /> Loading…</div>
          ) : (
            <>
              {/* ISP Provider + Speed + SLA */}
              <div className="isp-form-grid">
                <div className="isp-form-group isp-full">
                  <label><FaBuilding size={12} /> Service Provider</label>
                  <select
                    value={form.serviceProviderId}
                    onChange={e => updateField('serviceProviderId', e.target.value)}
                  >
                    <option value="">— Select Service Provider —</option>
                    {providers
                      .filter(p => ['IT', 'Telecoms'].includes(p.serviceCategory))
                      .map(p => (
                        <option key={p.serviceProviderId} value={p.serviceProviderId}>
                          {p.name} ({p.serviceCategory})
                        </option>
                      ))
                    }
                    {providers.filter(p => !['IT', 'Telecoms'].includes(p.serviceCategory)).length > 0 && (
                      <>
                        <option disabled>── Other providers ──</option>
                        {providers
                          .filter(p => !['IT', 'Telecoms'].includes(p.serviceCategory))
                          .map(p => (
                            <option key={p.serviceProviderId} value={p.serviceProviderId}>
                              {p.name} ({p.serviceCategory})
                            </option>
                          ))
                        }
                      </>
                    )}
                  </select>
                  {providers.length === 0 && (
                    <small className="isp-hint">
                      No providers found — add them in Settings → IT &amp; Network Security → Service Providers.
                    </small>
                  )}
                </div>

                {/* Throughput */}
                <div className="isp-form-group">
                  <label><FaTachometerAlt size={12} /> Expected Download (Mbps)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.expectedDownloadMbps}
                    onChange={e => updateField('expectedDownloadMbps', e.target.value)}
                    placeholder="e.g. 100"
                  />
                </div>

                <div className="isp-form-group">
                  <label><FaTachometerAlt size={12} /> Expected Upload (Mbps)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.expectedUploadMbps}
                    onChange={e => updateField('expectedUploadMbps', e.target.value)}
                    placeholder="e.g. 50"
                  />
                </div>

                {/* SLA */}
                <div className="isp-form-group">
                  <label><FaShieldAlt size={12} /> Uptime SLA (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.uptimeSlaPercent}
                    onChange={e => updateField('uptimeSlaPercent', e.target.value)}
                    placeholder="e.g. 99.9"
                  />
                </div>

                {/* Contract Expiry */}
                <div className="isp-form-group">
                  <label><FaCalendarAlt size={12} /> Contract Expiry Date</label>
                  <input
                    type="date"
                    value={form.contractExpiryDate}
                    onChange={e => updateField('contractExpiryDate', e.target.value)}
                  />
                  {form.contractExpiryDate && daysUntilExpiry !== null && (
                    <small className={`isp-expiry-hint ${expiryClass}`}>
                      {daysUntilExpiry < 0
                        ? `Expired ${Math.abs(daysUntilExpiry)} days ago`
                        : `${daysUntilExpiry} day(s) until expiry`}
                    </small>
                  )}
                </div>

                <div className="isp-form-group">
                  <label><FaCalendarAlt size={12} /> Reminder Days Before Expiry</label>
                  <input
                    type="number"
                    min="0"
                    value={form.contractExpiryReminderDays}
                    onChange={e => updateField('contractExpiryReminderDays', e.target.value)}
                    placeholder="e.g. 30"
                  />
                  <small className="isp-hint">
                    An action item will be auto-created this many days before expiry.
                  </small>
                </div>

                {/* Notes */}
                <div className="isp-form-group isp-full">
                  <label>Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={e => updateField('notes', e.target.value)}
                    placeholder="Account number, renewal terms, support reference…"
                    rows={3}
                  />
                </div>
              </div>

              {/* Documents */}
              <div className="isp-docs-section">
                <div className="isp-docs-header">
                  <span><FaFilePdf size={13} /> Contract Documents</span>
                  {contract?.internetServiceContractId && (
                    <label className="isp-upload-btn" title="Upload contract document">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        disabled={uploadingDoc}
                      />
                      <FaFileUpload size={13} />
                      {uploadingDoc ? 'Uploading…' : 'Upload'}
                    </label>
                  )}
                </div>
                {!contract?.internetServiceContractId && (
                  <p className="isp-hint">Save the contract first to enable document uploads.</p>
                )}
                {contract?.documents?.length > 0 ? (
                  <div className="isp-docs-list">
                    {contract.documents.map(doc => (
                      <div key={doc.serviceProviderDocumentId} className="isp-doc-item">
                        <span className="isp-doc-icon">
                          {doc.isPdf ? <FaFilePdf /> : <FaFileContract />}
                        </span>
                        <span className="isp-doc-name">{doc.fileName}</span>
                        <span className="isp-doc-size">{doc.fileSizeFormatted}</span>
                        {doc.isPdf && (
                          <button
                            className="isp-doc-btn"
                            onClick={() => setAnnotatingDoc(doc)}
                            title="Annotate / Fill & Sign"
                          >
                            <FaPen size={12} />
                          </button>
                        )}
                        <a
                          href={doc.downloadUrl}
                          className="isp-doc-btn"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Download"
                        >
                          <FaDownload size={12} />
                        </a>
                        <button
                          className="isp-doc-btn isp-doc-delete"
                          onClick={() => handleDeleteDoc(doc.serviceProviderDocumentId)}
                          title="Delete document"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : contract?.internetServiceContractId ? (
                  <p className="isp-hint">No documents attached yet.</p>
                ) : null}
              </div>

              {/* Contract History */}
              {contract?.internetServiceContractId && (
                <div className="isp-history-section">
                  <div className="isp-docs-header">
                    <span><FaHistory size={13} /> Change History</span>
                    <button
                      className="isp-upload-btn"
                      onClick={() => setShowHistory(v => !v)}
                      title={showHistory ? 'Hide history' : 'Show history'}
                    >
                      {showHistory ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {showHistory && (
                    loadingHistory ? (
                      <p className="isp-hint"><FaSpinner className="spin" /> Loading history…</p>
                    ) : history.length === 0 ? (
                      <p className="isp-hint">No history recorded yet.</p>
                    ) : (
                      <div className="isp-history-list">
                        {history.map(entry => (
                          <div key={entry.id} className="isp-history-entry">
                            <div className="isp-history-meta">
                              <span className={`isp-history-type isp-history-type--${entry.changeType.toLowerCase()}`}>
                                {entry.changeType}
                              </span>
                              <span className="isp-history-user">{entry.changedByUserName}</span>
                              <span className="isp-history-date">
                                {new Date(entry.changedAt).toLocaleString()}
                              </span>
                            </div>
                            {entry.fieldsChanged && (
                              <div className="isp-history-fields">
                                Changed: <em>{entry.fieldsChanged.split(',').join(', ')}</em>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="isp-modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || loading}>
            {saving ? <><FaSpinner className="spin" /> Saving…</> : <><FaSave size={13} /> Save Contract</>}
          </button>
        </div>

        {/* PDF Annotation Modal for contract documents */}
        {annotatingDoc && (
          <PdfAnnotationModal
            isOpen={!!annotatingDoc}
            onClose={() => setAnnotatingDoc(null)}
            attachment={{
              attachmentId: annotatingDoc.serviceProviderDocumentId,
              fileName: annotatingDoc.fileName,
              downloadUrl: annotatingDoc.downloadUrl,
              contentType: annotatingDoc.contentType
            }}
            contractDocumentMode={true}
          />
        )}
      </div>
    </div>
  );
};

// =====================================================================
// InterfacesPanel — Winbox-style tree with bridges + child ports
// =====================================================================
const InterfacesPanel = ({ routerId, showSuccess, showError }) => {
  const apiCall = useApiCall();
  const [interfaces, setInterfaces] = useState([]);
  const [bridges, setBridges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [savingAction, setSavingAction] = useState(null);
  const [expandedBridges, setExpandedBridges] = useState(new Set());

  // Inline edit states
  const [editingName, setEditingName] = useState(null);
  const [editNameVal, setEditNameVal] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentVal, setEditCommentVal] = useState('');

  // Bridge create form
  const [showCreateBridge, setShowCreateBridge] = useState(false);
  const [newBridge, setNewBridge] = useState({ name: '', comment: '', vlanFiltering: false });
  const [addingPort, setAddingPort] = useState(null);
  const [portInterface, setPortInterface] = useState('');

  // MTU editing
  const [editingMtu, setEditingMtu] = useState(null);
  const [editMtuVal, setEditMtuVal] = useState('');

  // Bridge settings editing
  const [editingBridge, setEditingBridge] = useState(null);
  const [editBridgeVals, setEditBridgeVals] = useState({});

  // Interface detail modal — stores the name of the interface being inspected
  const [detailInterfaceName, setDetailInterfaceName] = useState(null);

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    try {
      const [ifData, bData] = await Promise.all([
        apiCall(`/api/RouterConfig/${routerId}/interfaces`),
        apiCall(`/api/RouterConfig/${routerId}/bridges`)
      ]);
      setInterfaces(ifData.data || []);
      setBridges(bData.data || []);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setLoading(false); }
  }, [routerId, apiCall, showError]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  // ---- Interface actions ----
  const handleRename = async (ifId) => {
    if (!editNameVal.trim()) return;
    setSavingAction(`rename-${ifId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/interfaces/${encodeURIComponent(ifId)}/rename`, {
        method: 'PUT', body: JSON.stringify({ newName: editNameVal.trim() })
      });
      setEditingName(null); showSuccess('Interface renamed'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleComment = async (ifId) => {
    setSavingAction(`comment-${ifId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/interfaces/${encodeURIComponent(ifId)}/comment`, {
        method: 'PUT', body: JSON.stringify({ comment: editCommentVal })
      });
      setEditingComment(null); showSuccess('Comment updated'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleToggle = async (ifId, isDisabled) => {
    setSavingAction(`toggle-${ifId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/interfaces/${encodeURIComponent(ifId)}/toggle`, {
        method: 'PUT', body: JSON.stringify({ disabled: !isDisabled })
      });
      showSuccess(isDisabled ? 'Interface enabled' : 'Interface disabled'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleMtuSave = async (ifId) => {
    if (!editMtuVal.trim()) return;
    setSavingAction(`mtu-${ifId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/interfaces/${encodeURIComponent(ifId)}/mtu`, {
        method: 'PUT', body: JSON.stringify({ mtu: editMtuVal.trim() })
      });
      setEditingMtu(null); showSuccess('MTU updated'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleUpdateBridge = async (bridgeId) => {
    setSavingAction(`upd-bridge-${bridgeId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/bridges/${encodeURIComponent(bridgeId)}`, {
        method: 'PUT', body: JSON.stringify(editBridgeVals)
      });
      setEditingBridge(null); showSuccess('Bridge updated'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  // ---- Bridge actions ----
  const handleCreateBridge = async () => {
    if (!newBridge.name.trim()) return;
    setSavingAction('create-bridge');
    try {
      await apiCall(`/api/RouterConfig/${routerId}/bridges`, {
        method: 'POST', body: JSON.stringify(newBridge)
      });
      setShowCreateBridge(false);
      setNewBridge({ name: '', comment: '', vlanFiltering: false });
      showSuccess('Bridge created'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleRemoveBridge = async (bridgeId, name) => {
    if (!window.confirm(`Remove bridge "${name}"? All ports will be detached.`)) return;
    setSavingAction(`rm-bridge-${bridgeId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/bridges/${encodeURIComponent(bridgeId)}`, { method: 'DELETE' });
      showSuccess('Bridge removed'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleAddPort = async (bridgeName) => {
    if (!portInterface) return;
    setSavingAction(`add-port-${bridgeName}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/bridges/${encodeURIComponent(bridgeName)}/ports`, {
        method: 'POST', body: JSON.stringify({ interface: portInterface })
      });
      setAddingPort(null); setPortInterface('');
      showSuccess(`${portInterface} added to ${bridgeName}`); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleRemovePort = async (portId, ifName) => {
    if (!window.confirm(`Remove "${ifName}" from the bridge?`)) return;
    setSavingAction(`rm-port-${portId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/bridges/ports/${encodeURIComponent(portId)}`, { method: 'DELETE' });
      showSuccess('Port removed'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const toggleBridgeExpand = (name) => {
    setExpandedBridges(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  // Build used interfaces set for bridge port assignment
  const usedInterfaces = new Set(bridges.flatMap(b => (b.ports || []).map(p => p.interface)));
  const bridgeNames = new Set(bridges.map(b => b.name));
  const availableForPort = interfaces.filter(i => !usedInterfaces.has(i.name) && !bridgeNames.has(i.name));

  // Build the combined list: standalone interfaces + bridge groups
  const portInterfaces = new Set(bridges.flatMap(b => (b.ports || []).map(p => p.interface)));
  const standaloneInterfaces = interfaces.filter(
    i => !bridgeNames.has(i.name) && !portInterfaces.has(i.name)
  );

  // Apply filter
  const matchesFilter = (text) => !filter || text?.toLowerCase().includes(filter.toLowerCase());
  const filteredStandalone = standaloneInterfaces.filter(i =>
    matchesFilter(i.name) || matchesFilter(i.type) || matchesFilter(i.comment)
  );
  const filteredBridges = bridges.filter(b => {
    if (matchesFilter(b.name) || matchesFilter(b.comment)) return true;
    return (b.ports || []).some(p => matchesFilter(p.interface));
  });

  const statusDot = (running, disabled) => {
    if (disabled) return <span className="if-dot if-dot--disabled" title="Disabled" />;
    if (running) return <span className="if-dot if-dot--up" title="Running" />;
    return <span className="if-dot if-dot--down" title="Down" />;
  };

  const renderNameCell = (iface) => {
    if (editingName === iface.id) {
      return (
        <span className="inline-edit">
          <input value={editNameVal} onChange={e => setEditNameVal(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleRename(iface.id)} autoFocus />
          <button className="btn-icon" onClick={() => handleRename(iface.id)}
            disabled={savingAction === `rename-${iface.id}`}>
            {savingAction === `rename-${iface.id}` ? <FaSpinner className="spin" /> : <FaCheck />}
          </button>
          <button className="btn-icon" onClick={() => setEditingName(null)}><FaTimes /></button>
        </span>
      );
    }
    return (
      <span className="name-cell">
        <strong>{iface.name}</strong>
        {iface.defaultName && iface.defaultName !== iface.name && (
          <small className="muted"> ({iface.defaultName})</small>
        )}
        <button className="btn-icon btn-edit-inline"
          title="Rename" onClick={() => { setEditingName(iface.id); setEditNameVal(iface.name); }}>
          <FaEdit />
        </button>
      </span>
    );
  };

  const renderCommentCell = (iface) => {
    if (editingComment === iface.id) {
      return (
        <span className="inline-edit">
          <input value={editCommentVal} onChange={e => setEditCommentVal(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleComment(iface.id)}
            placeholder="Comment..." autoFocus />
          <button className="btn-icon" onClick={() => handleComment(iface.id)}
            disabled={savingAction === `comment-${iface.id}`}>
            {savingAction === `comment-${iface.id}` ? <FaSpinner className="spin" /> : <FaCheck />}
          </button>
          <button className="btn-icon" onClick={() => setEditingComment(null)}><FaTimes /></button>
        </span>
      );
    }
    return (
      <span className="comment-cell"
        onClick={() => { setEditingComment(iface.id); setEditCommentVal(iface.comment || ''); }}
        title="Click to edit">
        {iface.comment || <span className="muted">—</span>}
      </span>
    );
  };

  const renderMtuCell = (iface) => {
    if (editingMtu === iface.id) {
      return (
        <span className="inline-edit">
          <input value={editMtuVal} onChange={e => setEditMtuVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleMtuSave(iface.id); if (e.key === 'Escape') setEditingMtu(null); }}
            placeholder="1500" style={{ width: 70 }} autoFocus />
          <button className="btn-icon" onClick={() => handleMtuSave(iface.id)}
            disabled={savingAction === `mtu-${iface.id}`}>
            {savingAction === `mtu-${iface.id}` ? <FaSpinner className="spin" /> : <FaCheck />}
          </button>
          <button className="btn-icon" onClick={() => setEditingMtu(null)}><FaTimes /></button>
        </span>
      );
    }
    return (
      <span className="comment-cell"
        onClick={() => { setEditingMtu(iface.id); setEditMtuVal(iface.mtu || ''); }}
        title="Click to edit MTU">
        {iface.mtu || 'auto'}
      </span>
    );
  };

  return (
    <div className="panel">
      <div className="panel-toolbar">
        <input className="search-input" placeholder="Filter interfaces..."
          value={filter} onChange={(e) => setFilter(e.target.value)} />
        <div className="toolbar-actions">
          <button className="btn btn-outline btn-sm" onClick={() => setShowCreateBridge(!showCreateBridge)}>
            <FaSitemap /> New Bridge
          </button>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      {showCreateBridge && (
        <div className="add-form">
          <div className="form-row">
            <div className="form-group">
              <label>Bridge Name</label>
              <input placeholder="bridge1" value={newBridge.name}
                onChange={e => setNewBridge(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Comment</label>
              <input placeholder="Optional" value={newBridge.comment}
                onChange={e => setNewBridge(p => ({ ...p, comment: e.target.value }))} />
            </div>
            <div className="form-group" style={{ alignSelf: 'flex-end' }}>
              <label className="toggle-label">
                <input type="checkbox" checked={newBridge.vlanFiltering}
                  onChange={e => setNewBridge(p => ({ ...p, vlanFiltering: e.target.checked }))} />
                VLAN Filtering
              </label>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary btn-sm" onClick={handleCreateBridge}
                disabled={savingAction === 'create-bridge'}>
                {savingAction === 'create-bridge' ? <FaSpinner className="spin" /> : <FaCheck />} Create
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => setShowCreateBridge(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading && interfaces.length === 0 ? (
        <div className="empty">Loading interfaces...</div>
      ) : (filteredStandalone.length === 0 && filteredBridges.length === 0) ? (
        <div className="empty">{filter ? 'No interfaces match the filter' : 'No interfaces found'}</div>
      ) : (
        <div className="if-tree">
          <div className="if-tree__header">
            <div className="if-col if-col--status"></div>
            <div className="if-col if-col--name">Name</div>
            <div className="if-col if-col--type">Type</div>
            <div className="if-col if-col--mac">MAC Address</div>
            <div className="if-col if-col--mtu">MTU</div>
            <div className="if-col if-col--comment">Comment</div>
            <div className="if-col if-col--act-enable">Enable</div>
            <div className="if-col if-col--act-edit">Edit</div>
            <div className="if-col if-col--act-detail">Detail</div>
            <div className="if-col if-col--act-port">Port</div>
            <div className="if-col if-col--act-delete">Delete</div>
          </div>

          {/* Bridge groups */}
          {filteredBridges.map(bridge => {
            const isExpanded = expandedBridges.has(bridge.name);
            const portCount = bridge.ports?.length || 0;
            const bridgeIface = interfaces.find(i => i.name === bridge.name);

            return (
              <div key={bridge.id || bridge.name} className="if-tree__group">
                {/* Bridge header row */}
                <div className={`if-tree__row if-tree__row--bridge ${bridgeIface?.disabled ? 'if-tree__row--disabled' : ''}`}
                  onClick={() => toggleBridgeExpand(bridge.name)}>
                  <div className="if-col if-col--status">
                    {bridgeIface ? statusDot(bridgeIface.running, bridgeIface.disabled) : statusDot(bridge.running, bridge.disabled)}
                  </div>
                  <div className="if-col if-col--name">
                    <span className="if-tree__expand">
                      {portCount > 0 ? (isExpanded ? <FaChevronDown /> : <FaChevronRight />) : <span style={{ width: 14 }} />}
                    </span>
                    <FaSitemap className="if-icon if-icon--bridge" />
                    <strong>{bridge.name}</strong>
                    <span className="if-port-count">{portCount} port{portCount !== 1 ? 's' : ''}</span>
                    {bridge.vlanFiltering && <span className="badge badge-info">VLAN</span>}
                  </div>
                  <div className="if-col if-col--type"><span className="badge badge-type">bridge</span></div>
                  <div className="if-col if-col--mac mono">{bridgeIface?.macAddress || '—'}</div>
                  <div className="if-col if-col--mtu mono">{bridgeIface?.mtu || 'auto'}</div>
                  <div className="if-col if-col--comment">
                    {bridgeIface ? renderCommentCell(bridgeIface) : <span className="muted">{bridge.comment || '—'}</span>}
                  </div>
                  <div className="if-col if-col--act-enable" onClick={e => e.stopPropagation()}>
                    {bridgeIface && (
                      <button className="btn-icon" title={bridgeIface.disabled ? 'Enable bridge' : 'Disable bridge'}
                        onClick={() => handleToggle(bridgeIface.id, bridgeIface.disabled)} disabled={!!savingAction}>
                        {savingAction === `toggle-${bridgeIface.id}` ? <FaSpinner className="spin" /> :
                          bridgeIface.disabled ? <FaToggleOff className="toggle-off" /> : <FaToggleOn className="toggle-on" />}
                      </button>
                    )}
                  </div>
                  <div className="if-col if-col--act-edit" onClick={e => e.stopPropagation()}>
                    <button className="btn-icon" title="Edit bridge settings"
                      onClick={() => { setEditingBridge(bridge.id); setEditBridgeVals({ name: bridge.name, comment: bridge.comment || '', vlanFiltering: bridge.vlanFiltering }); }}>
                      <FaEdit />
                    </button>
                  </div>
                  <div className="if-col if-col--act-detail" onClick={e => e.stopPropagation()}>
                    <button className="btn-icon" title="View interface details"
                      onClick={() => setDetailInterfaceName(bridge.name)}>
                      <FaEye />
                    </button>
                  </div>
                  <div className="if-col if-col--act-port" onClick={e => e.stopPropagation()}>
                    <button className="btn-icon" title="Add port"
                      onClick={() => { setAddingPort(bridge.name); setPortInterface(''); }}>
                      <FaLink />
                    </button>
                  </div>
                  <div className="if-col if-col--act-delete" onClick={e => e.stopPropagation()}>
                    <button className="btn-icon btn-danger" title="Remove bridge"
                      onClick={() => handleRemoveBridge(bridge.id, bridge.name)} disabled={!!savingAction}>
                      {savingAction === `rm-bridge-${bridge.id}` ? <FaSpinner className="spin" /> : <FaTrash />}
                    </button>
                  </div>
                </div>

                {/* Bridge settings inline edit */}
                {editingBridge === bridge.id && (
                  <div className="port-add-row" onClick={e => e.stopPropagation()}>
                    <FaEdit className="muted" />
                    <input placeholder="Bridge name" value={editBridgeVals.name || ''}
                      onChange={e => setEditBridgeVals(v => ({ ...v, name: e.target.value }))}
                      style={{ flex: 1, maxWidth: 200, padding: '5px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13 }} />
                    <input placeholder="Comment" value={editBridgeVals.comment || ''}
                      onChange={e => setEditBridgeVals(v => ({ ...v, comment: e.target.value }))}
                      style={{ flex: 1, maxWidth: 200, padding: '5px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 13 }} />
                    <label className="toggle-label" style={{ fontSize: 12 }}>
                      <input type="checkbox" checked={editBridgeVals.vlanFiltering || false}
                        onChange={e => setEditBridgeVals(v => ({ ...v, vlanFiltering: e.target.checked }))} />
                      VLAN Filtering
                    </label>
                    <button className="btn btn-primary btn-xs" onClick={() => handleUpdateBridge(bridge.id)}
                      disabled={savingAction === `upd-bridge-${bridge.id}`}>
                      {savingAction === `upd-bridge-${bridge.id}` ? <FaSpinner className="spin" /> : <FaCheck />} Save
                    </button>
                    <button className="btn btn-outline btn-xs" onClick={() => setEditingBridge(null)}>Cancel</button>
                  </div>
                )}

                {/* Add port inline form */}
                {addingPort === bridge.name && (
                  <div className="port-add-row" onClick={e => e.stopPropagation()}>
                    <FaLink className="muted" />
                    <InterfaceStatusSelect
                      value={portInterface}
                      onChange={e => setPortInterface(e.target.value)}
                      interfaces={availableForPort}
                    />
                    <button className="btn btn-primary btn-xs" onClick={() => handleAddPort(bridge.name)}
                      disabled={!portInterface || savingAction === `add-port-${bridge.name}`}>
                      {savingAction === `add-port-${bridge.name}` ? <FaSpinner className="spin" /> : <FaCheck />} Add
                    </button>
                    <button className="btn btn-outline btn-xs" onClick={() => setAddingPort(null)}>Cancel</button>
                  </div>
                )}

                {/* Bridge port rows */}
                {isExpanded && (bridge.ports || []).map(port => {
                  const portIface = interfaces.find(i => i.name === port.interface);
                  const portIsDisabled = port.disabled || (portIface?.disabled ?? false);
                  return (
                    <div key={port.id} className={`if-tree__row if-tree__row--port ${portIsDisabled ? 'if-tree__row--disabled' : ''}`}>
                      <div className="if-col if-col--status">
                        {portIface ? statusDot(portIface.running, portIsDisabled) : <span className="if-dot if-dot--disabled" />}
                      </div>
                      <div className="if-col if-col--name if-col--indented">
                        <FaEthernet className="if-icon" />
                        {portIface ? renderNameCell(portIface) : <strong>{port.interface}</strong>}
                        {port.hw && <span className="badge badge-success">HW</span>}
                        {port.dynamic && <span className="badge badge-info">D</span>}
                        {port.disabled && <span className="badge badge-muted">Port off</span>}
                      </div>
                      <div className="if-col if-col--type">
                        <span className="badge badge-type">{portIface?.type || 'port'}</span>
                      </div>
                      <div className="if-col if-col--mac mono">{portIface?.macAddress || '—'}</div>
                      <div className="if-col if-col--mtu mono">
                        {portIface ? renderMtuCell(portIface) : (portIface?.mtu || 'auto')}
                      </div>
                      <div className="if-col if-col--comment">
                        {portIface ? renderCommentCell(portIface) : <span className="muted">—</span>}
                      </div>
                      <div className="if-col if-col--act-enable">
                        {portIface && (
                          <button className="btn-icon" title={portIface.disabled ? 'Enable interface' : 'Disable interface'}
                            onClick={() => handleToggle(portIface.id, portIface.disabled)} disabled={!!savingAction}>
                            {savingAction === `toggle-${portIface.id}` ? <FaSpinner className="spin" /> :
                              portIface.disabled ? <FaToggleOff className="toggle-off" /> : <FaToggleOn className="toggle-on" />}
                          </button>
                        )}
                      </div>
                      <div className="if-col if-col--act-edit"></div>
                      <div className="if-col if-col--act-detail">
                        <button className="btn-icon" title="View interface details"
                          onClick={() => setDetailInterfaceName(port.interface)}>
                          <FaEye />
                        </button>
                      </div>
                      <div className="if-col if-col--act-port"></div>
                      <div className="if-col if-col--act-delete">
                        {!port.dynamic && (
                          <button className="btn-icon btn-danger" title="Remove from bridge"
                            onClick={() => handleRemovePort(port.id, port.interface)} disabled={!!savingAction}>
                            {savingAction === `rm-port-${port.id}` ? <FaSpinner className="spin" /> : <FaUnlink />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Standalone interfaces (not part of any bridge) */}
          {filteredStandalone.map(iface => (
            <div key={iface.id || iface.name}
              className={`if-tree__row ${iface.disabled ? 'if-tree__row--disabled' : ''}`}>
              <div className="if-col if-col--status">
                {statusDot(iface.running, iface.disabled)}
              </div>
              <div className="if-col if-col--name">
                <FaEthernet className="if-icon" />
                {renderNameCell(iface)}
              </div>
              <div className="if-col if-col--type"><span className="badge badge-type">{iface.type}</span></div>
              <div className="if-col if-col--mac mono">{iface.macAddress || '—'}</div>
              <div className="if-col if-col--mtu mono">{renderMtuCell(iface)}</div>
              <div className="if-col if-col--comment">{renderCommentCell(iface)}</div>
              <div className="if-col if-col--act-enable">
                <button className="btn-icon" title={iface.disabled ? 'Enable' : 'Disable'}
                  onClick={() => handleToggle(iface.id, iface.disabled)} disabled={!!savingAction}>
                  {savingAction === `toggle-${iface.id}` ? <FaSpinner className="spin" /> :
                    iface.disabled ? <FaToggleOff className="toggle-off" /> : <FaToggleOn className="toggle-on" />}
                </button>
              </div>
              <div className="if-col if-col--act-edit"></div>
              <div className="if-col if-col--act-detail">
                <button className="btn-icon" title="View interface details"
                  onClick={() => setDetailInterfaceName(iface.name)}>
                  <FaEye />
                </button>
              </div>
              <div className="if-col if-col--act-port"></div>
              <div className="if-col if-col--act-delete"></div>
            </div>
          ))}
        </div>
      )}

      {/* Interface Detail Modal — opens when a user clicks the eye icon on any interface */}
      {detailInterfaceName && (
        <InterfaceDetailModal
          routerId={routerId}
          interfaceName={detailInterfaceName}
          onClose={() => setDetailInterfaceName(null)}
        />
      )}
    </div>
  );
};

// =====================================================================
// RFC1918 check — matches the backend NetworkDiagnosticsService.IsRfc1918
// =====================================================================
const isRfc1918 = (cidr) => {
  const ip = (cidr || '').split('/')[0];
  const parts = ip.split('.').map(Number);
  if (parts.length !== 4 || parts.some(p => isNaN(p))) return true; // treat invalid as private
  // 10.0.0.0/8
  if (parts[0] === 10) return true;
  // 172.16.0.0/12
  if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return true;
  // 192.168.0.0/16
  if (parts[0] === 192 && parts[1] === 168) return true;
  // 127.0.0.0/8 loopback
  if (parts[0] === 127) return true;
  // 169.254.0.0/16 link-local
  if (parts[0] === 169 && parts[1] === 254) return true;
  return false;
};

const stripCidr = (cidr) => (cidr || '').split('/')[0];

// =====================================================================
// IpAddressesPanel — unified view with auto-detected public IPs
// =====================================================================
const IpAddressesPanel = ({ routerId, showSuccess, showError }) => {
  const apiCall = useApiCall();
  const [addresses, setAddresses] = useState([]);
  const [interfaces, setInterfaces] = useState([]);
  const [publicAddresses, setPublicAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newAddr, setNewAddr] = useState({ address: '', interface: '', comment: '' });
  const [savingAction, setSavingAction] = useState(null);
  const syncedRef = useRef(false);
  const [editingComment, setEditingComment] = useState(null); // { addrId, value }
  // ISP contract modal — set to a public address object to open the contract editor
  const [ispContractTarget, setIspContractTarget] = useState(null);

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    try {
      const [addrData, ifData, pubData] = await Promise.all([
        apiCall(`/api/RouterConfig/${routerId}/ip-addresses`),
        apiCall(`/api/RouterConfig/${routerId}/interfaces`),
        apiCall(`/api/RouterConfig/${routerId}/public-addresses`)
      ]);
      setAddresses(addrData.data || []);
      setInterfaces(ifData.data || []);
      setPublicAddresses(pubData.data || []);
      return { addresses: addrData.data || [], publicAddresses: pubData.data || [] };
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setLoading(false); }
  }, [routerId, apiCall, showError]);

  // Auto-sync: create RouterPublicAddress entries for any non-RFC1918 router IPs
  // that don't already have one. Runs once after initial load.
  const autoSyncPublicAddresses = useCallback(async (routerAddrs, existingPublic) => {
    const existingIps = new Set((existingPublic || []).map(p => p.ipAddress));

    const publicRouterIps = (routerAddrs || [])
      .filter(a => !isRfc1918(a.address))
      .map(a => ({ ip: stripCidr(a.address), iface: a.interface, comment: a.comment }))
      .filter(a => !existingIps.has(a.ip));

    if (publicRouterIps.length === 0) return;

    for (const pub of publicRouterIps) {
      try {
        await apiCall(`/api/RouterConfig/${routerId}/public-addresses`, {
          method: 'POST',
          body: JSON.stringify({
            ipAddress: pub.ip,
            label: pub.comment || pub.iface || 'Public IP',
            monitorIcmp: true
          })
        });
      } catch (catchError) {
          Sentry.captureException(catchError);
        // Ignore duplicates / errors during auto-sync
      }
    }

    // Reload to show newly created entries
    await load();
  }, [routerId, apiCall, load]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      (async () => {
        const result = await load();
        if (result && !syncedRef.current) {
          syncedRef.current = true;
          await autoSyncPublicAddresses(result.addresses, result.publicAddresses);
        }
      })();
    }
  }, [load, autoSyncPublicAddresses]);

  const handleAdd = async () => {
    if (!newAddr.address.trim() || !newAddr.interface) return;
    setSavingAction('addip');
    try {
      await apiCall(`/api/RouterConfig/${routerId}/ip-addresses`, {
        method: 'POST', body: JSON.stringify(newAddr)
      });

      // If public IP, auto-create a monitored public address entry
      const bareIp = stripCidr(newAddr.address);
      if (!isRfc1918(newAddr.address)) {
        try {
          await apiCall(`/api/RouterConfig/${routerId}/public-addresses`, {
            method: 'POST',
            body: JSON.stringify({
              ipAddress: bareIp,
              label: newAddr.comment || newAddr.interface || 'Public IP',
              monitorIcmp: true
            })
          });
        } catch (catchError) {
            Sentry.captureException(catchError);
          // Already exists or other issue — not critical
        }
      }

      setShowAdd(false); setNewAddr({ address: '', interface: '', comment: '' });
      showSuccess('IP address added'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleRemove = async (addrId) => {
    if (!window.confirm('Remove this IP address?')) return;
    setSavingAction(`rmip-${addrId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/ip-addresses/${encodeURIComponent(addrId)}`, { method: 'DELETE' });
      showSuccess('IP address removed'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleToggleMonitor = async (pub) => {
    setSavingAction(`mon-${pub.id}`);
    try {
      await apiCall(`/api/RouterConfig/public-addresses/${pub.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ipAddress: pub.ipAddress, label: pub.label, monitorIcmp: !pub.monitorIcmp })
      });
      showSuccess(pub.monitorIcmp ? 'Monitoring disabled' : 'Monitoring enabled');
      load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleToggleAddress = async (addrId, isDisabled) => {
    setSavingAction(`toggle-${addrId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/ip-addresses/${encodeURIComponent(addrId)}/toggle`, {
        method: 'PUT', body: JSON.stringify({ disabled: !isDisabled })
      });
      showSuccess(isDisabled ? 'IP address enabled' : 'IP address disabled'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleSaveComment = async (addrId, comment) => {
    setSavingAction(`comment-${addrId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/ip-addresses/${encodeURIComponent(addrId)}/comment`, {
        method: 'PUT',
        body: JSON.stringify({ comment })
      });
      setEditingComment(null);
      showSuccess('Comment updated');
      load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  // Build a lookup from IP -> public address entry for inline display
  const pubByIp = {};
  for (const p of publicAddresses) { pubByIp[p.ipAddress] = p; }

  return (
    <div className="panel">
      <div className="panel-toolbar">
        <span className="panel-section-title">IP Addresses</span>
        <div className="toolbar-actions">
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
            <FaPlus /> Add Address
          </button>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="add-form">
          <div className="form-row">
            <div className="form-group">
              <label>Address (CIDR)</label>
              <input placeholder="192.168.1.1/24" value={newAddr.address}
                onChange={e => setNewAddr(p => ({ ...p, address: e.target.value }))} />
              {newAddr.address && !isRfc1918(newAddr.address) && (
                <small className="public-ip-hint">
                  <FaGlobe /> Public IP — ICMP monitoring will be enabled automatically
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Interface</label>
              <InterfaceStatusSelect
                value={newAddr.interface}
                onChange={e => setNewAddr(p => ({ ...p, interface: e.target.value }))}
                interfaces={interfaces}
              />
            </div>
            <div className="form-group">
              <label>Comment</label>
              <input placeholder="Optional" value={newAddr.comment}
                onChange={e => setNewAddr(p => ({ ...p, comment: e.target.value }))} />
            </div>
            <div className="form-actions">
              <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={savingAction === 'addip'}>
                {savingAction === 'addip' ? <FaSpinner className="spin" /> : <FaCheck />} Add
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading && addresses.length === 0 ? (
        <div className="empty">Loading IP addresses...</div>
      ) : addresses.length === 0 ? (
        <div className="empty">No IP addresses configured</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Address</th>
              <th>Network</th>
              <th>Interface</th>
              <th>Comment</th>
              <th className="col-center" style={{ width: 80 }}>Flags</th>
              <th className="col-center col-action" style={{ width: 50 }}>Contract</th>
              <th className="col-center col-action" style={{ width: 50 }}>Monitor</th>
              <th className="col-center col-action" style={{ width: 50 }}>Enable</th>
              <th className="col-center col-action" style={{ width: 50 }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {addresses.map(addr => {
              const bareIp = stripCidr(addr.address);
              const isPublic = !isRfc1918(addr.address);
              const pub = isPublic ? pubByIp[bareIp] : null;

              return (
                <tr key={addr.id} className={addr.disabled ? 'row-disabled' : ''}>
                  <td className="mono fw">{addr.address}</td>
                  <td className="mono">{addr.network}</td>
                  <td>{addr.interface}</td>
                  <td className="muted">
                    {editingComment?.addrId === addr.id ? (
                      <span className="inline-edit">
                        <input
                          value={editingComment.value}
                          onChange={e => setEditingComment(prev => ({ ...prev, value: e.target.value }))}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSaveComment(addr.id, editingComment.value);
                            if (e.key === 'Escape') setEditingComment(null);
                          }}
                          autoFocus
                          placeholder="Enter comment..."
                        />
                        <button className="btn-icon" title="Save" onClick={() => handleSaveComment(addr.id, editingComment.value)}
                          disabled={savingAction === `comment-${addr.id}`}>
                          {savingAction === `comment-${addr.id}` ? <FaSpinner className="spin" /> : <FaCheck />}
                        </button>
                        <button className="btn-icon" title="Cancel" onClick={() => setEditingComment(null)}>
                          <FaTimes />
                        </button>
                      </span>
                    ) : (
                      <span className="editable-cell" title="Click to edit comment"
                        onClick={() => setEditingComment({ addrId: addr.id, value: addr.comment || '' })}>
                        {addr.comment || '—'} <FaEdit style={{ fontSize: 10, opacity: 0.4, marginLeft: 4 }} />
                      </span>
                    )}
                  </td>
                  <td className="col-center">
                    {isPublic && <span className="badge badge-public" title="Public IP"><FaGlobe style={{ marginRight: 3, fontSize: 9 }} />Public</span>}
                    {addr.dynamic && <span className="badge badge-info">D</span>}
                    {addr.disabled && <span className="badge badge-muted">X</span>}
                  </td>
                  {/* Service Contract button — only shown for monitored public IPs */}
                  <td className="col-center col-action">
                    {pub ? (
                      <button
                        className="btn-icon btn-isp-contract"
                        title="View / edit service contract for this link"
                        onClick={() => setIspContractTarget(pub)}
                      >
                        <FaFileContract />
                      </button>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td className="col-center col-action">
                    {pub ? (
                      <button className="btn-icon" title={pub.monitorIcmp ? 'Disable ICMP monitoring' : 'Enable ICMP monitoring'}
                        onClick={() => handleToggleMonitor(pub)} disabled={!!savingAction}>
                        {savingAction === `mon-${pub.id}` ? <FaSpinner className="spin" /> :
                          pub.monitorIcmp ? <FaSignal className="toggle-on" /> : <FaSignal className="toggle-off" />}
                      </button>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td className="col-center col-action">
                    {!addr.dynamic ? (
                      <button className="btn-icon" title={addr.disabled ? 'Enable' : 'Disable'}
                        onClick={() => handleToggleAddress(addr.id, addr.disabled)} disabled={!!savingAction}>
                        {savingAction === `toggle-${addr.id}` ? <FaSpinner className="spin" /> :
                          addr.disabled ? <FaToggleOff className="toggle-off" /> : <FaToggleOn className="toggle-on" />}
                      </button>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td className="col-center col-action">
                    {!addr.dynamic ? (
                      <button className="btn-icon btn-danger" title="Remove"
                        onClick={() => handleRemove(addr.id)} disabled={!!savingAction}>
                        {savingAction === `rmip-${addr.id}` ? <FaSpinner className="spin" /> : <FaTrash />}
                      </button>
                    ) : <span className="muted">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ISP Contract modal — opened when user clicks the contract button on a public IP */}
      {ispContractTarget && (
        <IspContractModal
          publicAddress={ispContractTarget}
          onClose={() => setIspContractTarget(null)}
        />
      )}
    </div>
  );
};

// =====================================================================
// DhcpPanel — DHCP servers, clients, leases, and pools
// =====================================================================
const DhcpPanel = ({ routerId, showSuccess, showError }) => {
  const apiCall = useApiCall();
  const [servers, setServers] = useState([]);
  const [clients, setClients] = useState([]);
  const [leases, setLeases] = useState([]);
  const [pools, setPools] = useState([]);
  const [interfaces, setInterfaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState(null);
  const [activeView, setActiveView] = useState('servers');
  const [filter, setFilter] = useState('');
  const [showCreateServer, setShowCreateServer] = useState(false);
  const [newServer, setNewServer] = useState({
    name: '', interface: '', poolName: '', poolRanges: '',
    networkAddress: '', gateway: '', dnsServer: '', leaseTime: '1d'
  });

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    try {
      const [sData, cData, lData, pData, ifData] = await Promise.all([
        apiCall(`/api/RouterConfig/${routerId}/dhcp-servers`),
        apiCall(`/api/RouterConfig/${routerId}/dhcp-clients`),
        apiCall(`/api/RouterConfig/${routerId}/dhcp-leases`),
        apiCall(`/api/RouterConfig/${routerId}/ip-pools`),
        apiCall(`/api/RouterConfig/${routerId}/interfaces`)
      ]);
      setServers(sData.data || []);
      setClients(cData.data || []);
      setLeases(lData.data || []);
      setPools(pData.data || []);
      setInterfaces(ifData.data || []);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setLoading(false); }
  }, [routerId, apiCall, showError]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  const handleToggleServer = async (serverId, isDisabled) => {
    setSavingAction(`toggle-${serverId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dhcp-servers/${encodeURIComponent(serverId)}/toggle`, {
        method: 'PUT', body: JSON.stringify({ disabled: !isDisabled })
      });
      showSuccess(isDisabled ? 'DHCP server enabled' : 'DHCP server disabled'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleRemoveServer = async (serverId, name) => {
    if (!window.confirm(`Remove DHCP server "${name}"?`)) return;
    setSavingAction(`rm-${serverId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dhcp-servers/${encodeURIComponent(serverId)}`, { method: 'DELETE' });
      showSuccess('DHCP server removed'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleCreateServer = async () => {
    if (!newServer.name.trim() || !newServer.interface) return;
    setSavingAction('create-dhcp');
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dhcp-servers`, {
        method: 'POST', body: JSON.stringify(newServer)
      });
      setShowCreateServer(false);
      setNewServer({ name: '', interface: '', poolName: '', poolRanges: '', networkAddress: '', gateway: '', dnsServer: '', leaseTime: '1d' });
      showSuccess('DHCP server created'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleMakeStatic = async (leaseId) => {
    setSavingAction(`static-${leaseId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dhcp-leases/${encodeURIComponent(leaseId)}/make-static`, { method: 'POST' });
      showSuccess('Lease made static'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleRemoveLease = async (leaseId) => {
    if (!window.confirm('Remove this DHCP lease?')) return;
    setSavingAction(`rm-lease-${leaseId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dhcp-leases/${encodeURIComponent(leaseId)}`, { method: 'DELETE' });
      showSuccess('Lease removed'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleToggleClient = async (clientId, isDisabled) => {
    setSavingAction(`toggle-client-${clientId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dhcp-clients/${encodeURIComponent(clientId)}/toggle`, {
        method: 'PUT', body: JSON.stringify({ disabled: !isDisabled })
      });
      showSuccess(isDisabled ? 'DHCP client enabled' : 'DHCP client disabled'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleUpdateClientSetting = async (clientId, setting, value) => {
    setSavingAction(`upd-client-${clientId}-${setting}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dhcp-clients/${encodeURIComponent(clientId)}`, {
        method: 'PUT', body: JSON.stringify({ [setting]: value })
      });
      showSuccess(`DHCP client ${setting} updated`); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const filteredLeases = leases.filter(l =>
    !filter || l.address?.toLowerCase().includes(filter.toLowerCase()) ||
    l.macAddress?.toLowerCase().includes(filter.toLowerCase()) ||
    l.hostName?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="panel">
      <div className="panel-toolbar">
        <div className="view-toggle">
          {['servers', 'clients', 'leases', 'pools'].map(v => (
            <button key={v} className={`btn btn-sm ${activeView === v ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveView(v)}>
              {v === 'servers' ? `Servers (${servers.length})` : v === 'clients' ? `Clients (${clients.length})` : v === 'leases' ? `Leases (${leases.length})` : `Pools (${pools.length})`}
            </button>
          ))}
        </div>
        <div className="toolbar-actions">
          {activeView === 'servers' && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreateServer(!showCreateServer)}>
              <FaPlus /> New Server
            </button>
          )}
          {activeView === 'leases' && (
            <input className="search-input" placeholder="Filter leases..."
              value={filter} onChange={e => setFilter(e.target.value)} style={{ width: 200 }} />
          )}
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      {/* DHCP Server creation form */}
      {showCreateServer && (
        <div className="add-form">
          <div className="form-row">
            <div className="form-group">
              <label>Server Name</label>
              <input placeholder="dhcp1" value={newServer.name}
                onChange={e => setNewServer(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Interface</label>
              <InterfaceStatusSelect
                value={newServer.interface}
                onChange={e => setNewServer(p => ({ ...p, interface: e.target.value }))}
                interfaces={interfaces}
              />
            </div>
            <div className="form-group">
              <label>Pool Name</label>
              <input placeholder="dhcp_pool1" value={newServer.poolName}
                onChange={e => setNewServer(p => ({ ...p, poolName: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Pool Ranges</label>
              <input placeholder="192.168.1.100-192.168.1.200" value={newServer.poolRanges}
                onChange={e => setNewServer(p => ({ ...p, poolRanges: e.target.value }))} />
            </div>
          </div>
          <div className="form-row" style={{ marginTop: 8 }}>
            <div className="form-group">
              <label>Network Address</label>
              <input placeholder="192.168.1.0/24" value={newServer.networkAddress}
                onChange={e => setNewServer(p => ({ ...p, networkAddress: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Gateway</label>
              <input placeholder="192.168.1.1" value={newServer.gateway}
                onChange={e => setNewServer(p => ({ ...p, gateway: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>DNS Server</label>
              <input placeholder="8.8.8.8" value={newServer.dnsServer}
                onChange={e => setNewServer(p => ({ ...p, dnsServer: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Lease Time</label>
              <input placeholder="1d" value={newServer.leaseTime}
                onChange={e => setNewServer(p => ({ ...p, leaseTime: e.target.value }))} />
            </div>
            <div className="form-actions">
              <button className="btn btn-primary btn-sm" onClick={handleCreateServer}
                disabled={savingAction === 'create-dhcp'}>
                {savingAction === 'create-dhcp' ? <FaSpinner className="spin" /> : <FaCheck />} Create
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => setShowCreateServer(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading && servers.length === 0 ? (
        <div className="empty">Loading DHCP configuration...</div>
      ) : activeView === 'servers' ? (
        servers.length === 0 ? <div className="empty">No DHCP servers configured</div> : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th><th>Interface</th><th>Address Pool</th><th>Lease Time</th><th>Status</th>
                <th className="col-center col-action" style={{ width: 50 }}>Enable</th>
                <th className="col-center col-action" style={{ width: 50 }}>Delete</th>
              </tr>
            </thead>
            <tbody>
              {servers.map(s => (
                <tr key={s.id} className={s.disabled ? 'row-disabled' : ''}>
                  <td className="fw">{s.name}</td>
                  <td>{s.interface}</td>
                  <td>{s.addressPool}</td>
                  <td className="mono">{s.leaseTime}</td>
                  <td>
                    {s.disabled ? <span className="badge badge-muted">Disabled</span> : <span className="badge badge-success">Active</span>}
                  </td>
                  <td className="col-center col-action">
                    <button className="btn-icon" title={s.disabled ? 'Enable' : 'Disable'}
                      onClick={() => handleToggleServer(s.id, s.disabled)} disabled={!!savingAction}>
                      {savingAction === `toggle-${s.id}` ? <FaSpinner className="spin" /> :
                        s.disabled ? <FaToggleOff className="toggle-off" /> : <FaToggleOn className="toggle-on" />}
                    </button>
                  </td>
                  <td className="col-center col-action">
                    <button className="btn-icon btn-danger" title="Remove"
                      onClick={() => handleRemoveServer(s.id, s.name)} disabled={!!savingAction}>
                      {savingAction === `rm-${s.id}` ? <FaSpinner className="spin" /> : <FaTrash />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : activeView === 'clients' ? (
        clients.length === 0 ? <div className="empty">No DHCP clients configured</div> : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Interface</th><th>Address</th><th>Gateway</th><th>DNS</th><th>Expires</th><th>Status</th>
                <th className="col-center col-action" style={{ width: 50 }} title="Use Peer DNS">DNS</th>
                <th className="col-center col-action" style={{ width: 50 }} title="Use Peer NTP">NTP</th>
                <th className="col-center col-action" style={{ width: 120 }} title="Add Default Route">Default Route</th>
                <th className="col-center col-action" style={{ width: 50 }}>Enable</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.id} className={c.disabled ? 'row-disabled' : ''}>
                  <td className="fw">{c.interface}</td>
                  <td className="mono">{c.address || <span className="muted">—</span>}</td>
                  <td className="mono">{c.gateway || <span className="muted">—</span>}</td>
                  <td className="mono">{c.dnsServer || <span className="muted">—</span>}</td>
                  <td className="mono">{c.expiresAfter || <span className="muted">—</span>}</td>
                  <td>
                    {c.disabled ? <span className="badge badge-muted">Disabled</span> :
                      c.status === 'bound' ? <span className="badge badge-success">Bound</span> :
                      c.status === 'searching' ? <span className="badge badge-info">Searching</span> :
                      <span className="badge badge-muted">{c.status || 'Unknown'}</span>}
                  </td>
                  <td className="col-center col-action">
                    <button className="btn-icon" title={c.usePeerDns ? 'Disable Use Peer DNS' : 'Enable Use Peer DNS'}
                      onClick={() => handleUpdateClientSetting(c.id, 'usePeerDns', !c.usePeerDns)} disabled={!!savingAction}>
                      {savingAction === `upd-client-${c.id}-usePeerDns` ? <FaSpinner className="spin" /> :
                        c.usePeerDns ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                    </button>
                  </td>
                  <td className="col-center col-action">
                    <button className="btn-icon" title={c.usePeerNtp ? 'Disable Use Peer NTP' : 'Enable Use Peer NTP'}
                      onClick={() => handleUpdateClientSetting(c.id, 'usePeerNtp', !c.usePeerNtp)} disabled={!!savingAction}>
                      {savingAction === `upd-client-${c.id}-usePeerNtp` ? <FaSpinner className="spin" /> :
                        c.usePeerNtp ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                    </button>
                  </td>
                  <td className="col-center col-action">
                    <select className="dhcp-client-select"
                      value={c.addDefaultRoute || 'yes'}
                      onChange={e => handleUpdateClientSetting(c.id, 'addDefaultRoute', e.target.value)}
                      disabled={!!savingAction}
                      title="Add Default Route mode">
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                      <option value="special-classless">Classless</option>
                    </select>
                  </td>
                  <td className="col-center col-action">
                    <button className="btn-icon" title={c.disabled ? 'Enable' : 'Disable'}
                      onClick={() => handleToggleClient(c.id, c.disabled)} disabled={!!savingAction}>
                      {savingAction === `toggle-client-${c.id}` ? <FaSpinner className="spin" /> :
                        c.disabled ? <FaToggleOff className="toggle-off" /> : <FaToggleOn className="toggle-on" />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : activeView === 'leases' ? (
        filteredLeases.length === 0 ? <div className="empty">{filter ? 'No leases match filter' : 'No active leases'}</div> : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Address</th><th>MAC Address</th><th>Hostname</th><th>Server</th><th>Status</th><th>Expires</th>
                <th className="col-center col-action" style={{ width: 50 }}>Static</th>
                <th className="col-center col-action" style={{ width: 50 }}>Delete</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeases.map(l => (
                <tr key={l.id} className={l.status === 'waiting' ? 'row-disabled' : ''}>
                  <td className="mono fw">{l.address}</td>
                  <td className="mono">{l.macAddress || '—'}</td>
                  <td>{l.hostName || <span className="muted">—</span>}</td>
                  <td>{l.server}</td>
                  <td>
                    <span className={`badge ${l.status === 'bound' ? 'badge-success' : l.status === 'waiting' ? 'badge-muted' : 'badge-info'}`}>
                      {l.status || 'unknown'}
                    </span>
                    {l.dynamic === false && <span className="badge badge-type" style={{ marginLeft: 4 }}>Static</span>}
                  </td>
                  <td className="mono">{l.expiresAfter || '—'}</td>
                  <td className="col-center col-action">
                    {l.dynamic !== false ? (
                      <button className="btn-icon" title="Make static"
                        onClick={() => handleMakeStatic(l.id)} disabled={!!savingAction}>
                        {savingAction === `static-${l.id}` ? <FaSpinner className="spin" /> : <FaLock />}
                      </button>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td className="col-center col-action">
                    <button className="btn-icon btn-danger" title="Remove lease"
                      onClick={() => handleRemoveLease(l.id)} disabled={!!savingAction}>
                      {savingAction === `rm-lease-${l.id}` ? <FaSpinner className="spin" /> : <FaTrash />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : (
        pools.length === 0 ? <div className="empty">No IP pools configured</div> : (
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Ranges</th><th>Next Pool</th></tr>
            </thead>
            <tbody>
              {pools.map(p => (
                <tr key={p.id}>
                  <td className="fw">{p.name}</td>
                  <td className="mono">{p.ranges}</td>
                  <td>{p.nextPool || <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
};

// =====================================================================
// RoutesPanel — Static route management
// =====================================================================
const RoutesPanel = ({ routerId, showSuccess, showError }) => {
  const apiCall = useApiCall();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newRoute, setNewRoute] = useState({ dstAddress: '0.0.0.0/0', gateway: '', distance: '1', routingMark: '', comment: '' });
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    try {
      const data = await apiCall(`/api/RouterConfig/${routerId}/routes`);
      setRoutes(data.data || []);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setLoading(false); }
  }, [routerId, apiCall, showError]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  const handleAdd = async () => {
    if (!newRoute.dstAddress.trim() || !newRoute.gateway.trim()) return;
    setSavingAction('addroute');
    try {
      await apiCall(`/api/RouterConfig/${routerId}/routes`, {
        method: 'POST', body: JSON.stringify(newRoute)
      });
      setShowAdd(false); setNewRoute({ dstAddress: '0.0.0.0/0', gateway: '', distance: '1', routingMark: '', comment: '' });
      showSuccess('Route added'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleRemove = async (routeId) => {
    if (!window.confirm('Remove this route?')) return;
    setSavingAction(`rm-${routeId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/routes/${encodeURIComponent(routeId)}`, { method: 'DELETE' });
      showSuccess('Route removed'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleToggle = async (routeId, isDisabled) => {
    setSavingAction(`toggle-${routeId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/routes/${encodeURIComponent(routeId)}/toggle`, {
        method: 'PUT', body: JSON.stringify({ disabled: !isDisabled })
      });
      showSuccess(isDisabled ? 'Route enabled' : 'Route disabled'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const filtered = routes.filter(r =>
    !filter || r.dstAddress?.toLowerCase().includes(filter.toLowerCase()) ||
    r.gateway?.toLowerCase().includes(filter.toLowerCase()) ||
    r.comment?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="panel">
      <div className="panel-toolbar">
        <input className="search-input" placeholder="Filter routes..."
          value={filter} onChange={e => setFilter(e.target.value)} />
        <div className="toolbar-actions">
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
            <FaPlus /> Add Route
          </button>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="add-form">
          <div className="form-row">
            <div className="form-group">
              <label>Destination</label>
              <input placeholder="0.0.0.0/0" value={newRoute.dstAddress}
                onChange={e => setNewRoute(p => ({ ...p, dstAddress: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Gateway</label>
              <input placeholder="192.168.1.1" value={newRoute.gateway}
                onChange={e => setNewRoute(p => ({ ...p, gateway: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Distance</label>
              <input type="number" placeholder="1" value={newRoute.distance}
                onChange={e => setNewRoute(p => ({ ...p, distance: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Comment</label>
              <input placeholder="Optional" value={newRoute.comment}
                onChange={e => setNewRoute(p => ({ ...p, comment: e.target.value }))} />
            </div>
            <div className="form-actions">
              <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={savingAction === 'addroute'}>
                {savingAction === 'addroute' ? <FaSpinner className="spin" /> : <FaCheck />} Add
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading && routes.length === 0 ? (
        <div className="empty">Loading routes...</div>
      ) : filtered.length === 0 ? (
        <div className="empty">{filter ? 'No routes match filter' : 'No routes found'}</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Destination</th><th>Gateway</th><th>Distance</th><th>Routing Mark</th><th>Comment</th>
              <th className="col-center" style={{ width: 80 }}>Flags</th>
              <th className="col-center col-action" style={{ width: 50 }}>Enable</th>
              <th className="col-center col-action" style={{ width: 50 }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className={r.disabled ? 'row-disabled' : ''}>
                <td className="mono fw">{r.dstAddress}</td>
                <td className="mono">{r.gateway || <span className="muted">—</span>}</td>
                <td className="mono">{r.distance}</td>
                <td>{r.routingMark || <span className="muted">—</span>}</td>
                <td className="muted">{r.comment || '—'}</td>
                <td className="col-center">
                  {r.active && <span className="badge badge-success">A</span>}
                  {r.dynamic && <span className="badge badge-info">D</span>}
                  {r.disabled && <span className="badge badge-muted">X</span>}
                </td>
                <td className="col-center col-action">
                  {!r.dynamic ? (
                    <button className="btn-icon" title={r.disabled ? 'Enable' : 'Disable'}
                      onClick={() => handleToggle(r.id, r.disabled)} disabled={!!savingAction}>
                      {savingAction === `toggle-${r.id}` ? <FaSpinner className="spin" /> :
                        r.disabled ? <FaToggleOff className="toggle-off" /> : <FaToggleOn className="toggle-on" />}
                    </button>
                  ) : <span className="muted">—</span>}
                </td>
                <td className="col-center col-action">
                  {!r.dynamic ? (
                    <button className="btn-icon btn-danger" title="Remove"
                      onClick={() => handleRemove(r.id)} disabled={!!savingAction}>
                      {savingAction === `rm-${r.id}` ? <FaSpinner className="spin" /> : <FaTrash />}
                    </button>
                  ) : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// =====================================================================
// DnsPanel — DNS settings and static entries
// =====================================================================
const DnsPanel = ({ routerId, showSuccess, showError }) => {
  const apiCall = useApiCall();
  const [settings, setSettings] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newEntry, setNewEntry] = useState({ name: '', address: '', comment: '' });
  const [editingServers, setEditingServers] = useState(false);
  const [serversVal, setServersVal] = useState('');
  const [allowRemote, setAllowRemote] = useState(false);

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    try {
      const data = await apiCall(`/api/RouterConfig/${routerId}/dns`);
      setSettings(data.data?.settings || null);
      setEntries(data.data?.entries || []);
      setServersVal(data.data?.settings?.servers || '');
      setAllowRemote(data.data?.settings?.allowRemoteRequests || false);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setLoading(false); }
  }, [routerId, apiCall, showError]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  const handleSaveSettings = async () => {
    setSavingAction('dns-settings');
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dns`, {
        method: 'PUT', body: JSON.stringify({ servers: serversVal, allowRemoteRequests: allowRemote })
      });
      setEditingServers(false); showSuccess('DNS settings updated'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleAddEntry = async () => {
    if (!newEntry.name.trim() || !newEntry.address.trim()) return;
    setSavingAction('add-dns');
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dns/static`, {
        method: 'POST', body: JSON.stringify(newEntry)
      });
      setShowAdd(false); setNewEntry({ name: '', address: '', comment: '' });
      showSuccess('DNS entry added'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleRemoveEntry = async (entryId) => {
    if (!window.confirm('Remove this DNS entry?')) return;
    setSavingAction(`rm-dns-${entryId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dns/static/${encodeURIComponent(entryId)}`, { method: 'DELETE' });
      showSuccess('DNS entry removed'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleToggleDnsEntry = async (entryId, isDisabled) => {
    setSavingAction(`toggle-dns-${entryId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/dns/static/${encodeURIComponent(entryId)}/toggle`, {
        method: 'PUT', body: JSON.stringify({ disabled: !isDisabled })
      });
      showSuccess(isDisabled ? 'DNS entry enabled' : 'DNS entry disabled'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  return (
    <div className="panel">
      <div className="panel-toolbar">
        <span className="panel-section-title">DNS Configuration</span>
        <div className="toolbar-actions">
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      {/* DNS Settings Card */}
      {settings && (
        <div className="dns-settings-card">
          <div className="dns-settings-row">
            <div className="dns-field">
              <label>DNS Servers</label>
              {editingServers ? (
                <div className="inline-edit">
                  <input value={serversVal} onChange={e => setServersVal(e.target.value)}
                    placeholder="8.8.8.8,8.8.4.4" style={{ flex: 1 }} />
                  <label className="toggle-label" style={{ marginLeft: 10 }}>
                    <input type="checkbox" checked={allowRemote} onChange={e => setAllowRemote(e.target.checked)} />
                    Allow Remote Requests
                  </label>
                  <button className="btn btn-primary btn-xs" onClick={handleSaveSettings}
                    disabled={savingAction === 'dns-settings'}>
                    {savingAction === 'dns-settings' ? <FaSpinner className="spin" /> : <FaCheck />} Save
                  </button>
                  <button className="btn btn-outline btn-xs" onClick={() => setEditingServers(false)}>Cancel</button>
                </div>
              ) : (
                <div className="dns-value" onClick={() => setEditingServers(true)} title="Click to edit">
                  <span className="mono">{settings.servers || <span className="muted">Not configured</span>}</span>
                  {settings.dynamicServers && (
                    <small className="muted"> (dynamic: {settings.dynamicServers})</small>
                  )}
                  <FaEdit style={{ fontSize: 10, opacity: 0.4, marginLeft: 6 }} />
                </div>
              )}
            </div>
            <div className="dns-field">
              <label>Allow Remote Requests</label>
              <span className={`badge ${settings.allowRemoteRequests ? 'badge-success' : 'badge-muted'}`}>
                {settings.allowRemoteRequests ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="dns-field">
              <label>Cache</label>
              <span className="mono">{settings.cacheUsed} / {settings.cacheSize} KiB</span>
            </div>
          </div>
        </div>
      )}

      {/* Static DNS Entries */}
      <div className="panel-toolbar panel-toolbar--section">
        <span className="panel-section-title">Static DNS Entries</span>
        <div className="toolbar-actions">
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
            <FaPlus /> Add Entry
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="add-form">
          <div className="form-row">
            <div className="form-group">
              <label>Name (FQDN)</label>
              <input placeholder="host.example.com" value={newEntry.name}
                onChange={e => setNewEntry(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Address</label>
              <input placeholder="192.168.1.100" value={newEntry.address}
                onChange={e => setNewEntry(p => ({ ...p, address: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Comment</label>
              <input placeholder="Optional" value={newEntry.comment}
                onChange={e => setNewEntry(p => ({ ...p, comment: e.target.value }))} />
            </div>
            <div className="form-actions">
              <button className="btn btn-primary btn-sm" onClick={handleAddEntry} disabled={savingAction === 'add-dns'}>
                {savingAction === 'add-dns' ? <FaSpinner className="spin" /> : <FaCheck />} Add
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading && entries.length === 0 ? (
        <div className="empty">Loading DNS entries...</div>
      ) : entries.length === 0 ? (
        <div className="empty">No static DNS entries</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th><th>Address</th><th>TTL</th><th>Comment</th>
              <th className="col-center" style={{ width: 80 }}>Flags</th>
              <th className="col-center col-action" style={{ width: 50 }}>Enable</th>
              <th className="col-center col-action" style={{ width: 50 }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id} className={e.disabled ? 'row-disabled' : ''}>
                <td className="fw">{e.name}</td>
                <td className="mono">{e.address}</td>
                <td className="mono">{e.ttl || '—'}</td>
                <td className="muted">{e.comment || '—'}</td>
                <td className="col-center">
                  {e.dynamic && <span className="badge badge-info">D</span>}
                  {e.disabled && <span className="badge badge-muted">X</span>}
                </td>
                <td className="col-center col-action">
                  {!e.dynamic ? (
                    <button className="btn-icon" title={e.disabled ? 'Enable' : 'Disable'}
                      onClick={() => handleToggleDnsEntry(e.id, e.disabled)} disabled={!!savingAction}>
                      {savingAction === `toggle-dns-${e.id}` ? <FaSpinner className="spin" /> :
                        e.disabled ? <FaToggleOff className="toggle-off" /> : <FaToggleOn className="toggle-on" />}
                    </button>
                  ) : <span className="muted">—</span>}
                </td>
                <td className="col-center col-action">
                  {!e.dynamic ? (
                    <button className="btn-icon btn-danger" title="Remove"
                      onClick={() => handleRemoveEntry(e.id)} disabled={!!savingAction}>
                      {savingAction === `rm-dns-${e.id}` ? <FaSpinner className="spin" /> : <FaTrash />}
                    </button>
                  ) : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// =====================================================================
// NotesPanel — Markdown notes editor with live preview
// Exported for reuse in RouterDetailPage (Intelligence tab).
// =====================================================================
export const NotesPanel = ({ routerConfig, onConfigUpdate, showSuccess, showError }) => {
  const apiCall = useApiCall();
  const [savingAction, setSavingAction] = useState(null);
  const [notes, setNotes] = useState(routerConfig?.notes || '');
  const [mode, setMode] = useState('edit'); // 'edit' | 'preview' | 'split'
  const [hasChanges, setHasChanges] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (routerConfig) {
      (async () => {
        await Promise.resolve();
        setNotes(routerConfig.notes || '');
        setHasChanges(false);
      })();
    }
  }, [routerConfig]);

  const handleNotesChange = useCallback((val) => {
    setNotes(val);
    setHasChanges(val !== (routerConfig?.notes || ''));
  }, [routerConfig]);

  const handleSave = async () => {
    setSavingAction('notes');
    try {
      const data = await apiCall('/api/RouterConfig', {
        method: 'POST',
        body: JSON.stringify({ ...routerConfig, notes })
      });
      showSuccess('Notes saved');
      setHasChanges(false);
      if (onConfigUpdate && data.data) onConfigUpdate(data.data);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const insertMarkdown = useCallback((before, after = '', placeholder = '') => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = notes.substring(start, end) || placeholder;
    const newText = notes.substring(0, start) + before + selected + after + notes.substring(end);
    handleNotesChange(newText);
    setTimeout(() => {
      ta.focus();
      const cursorPos = start + before.length + selected.length + after.length;
      ta.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  }, [notes, handleNotesChange]);

  // Static toolbar config — args only, no closures over refs
  const NOTES_TOOLBAR = [
    { icon: <FaHeading />, title: 'Heading',        before: '## ',                    after: '',          placeholder: 'Heading'    },
    { icon: <FaBold />,    title: 'Bold',           before: '**',                    after: '**',        placeholder: 'bold text'  },
    { icon: <FaItalic />,  title: 'Italic',         before: '_',                     after: '_',         placeholder: 'italic text'},
    { icon: <FaCode />,    title: 'Code',           before: '`',                     after: '`',         placeholder: 'code'       },
    { sep: true },
    { icon: <FaListUl />,  title: 'Bullet list',    before: '- ',                    after: '',          placeholder: 'item'       },
    { icon: <FaListOl />,  title: 'Numbered list',  before: '1. ',                   after: '',          placeholder: 'item'       },
    { icon: <FaQuoteRight />, title: 'Blockquote',  before: '> ',                    after: '',          placeholder: 'quote'      },
    { icon: <FaMinus />,   title: 'Horizontal rule',before: '\n---\n',               after: '',          placeholder: ''           },
    { sep: true },
    { icon: <FaLink />,    title: 'Link',           before: '[',                     after: '](url)',    placeholder: 'link text'  },
    { icon: <FaImage />,   title: 'Image',          before: '![',                    after: '](url)',    placeholder: 'alt text'   },
    { icon: <FaTable />,   title: 'Table',          before: '\n| Column 1 | Column 2 | Column 3 |\n| --- | --- | --- |\n| ', after: ' | | |\n', placeholder: 'data' },
  ];

  return (
    <div className="panel notes-panel">
      <div className="panel-toolbar">
        <div className="view-toggle">
          <button className={`btn btn-sm ${mode === 'edit' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMode('edit')} title="Edit mode">
            <FaPen /> Edit
          </button>
          <button className={`btn btn-sm ${mode === 'split' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMode('split')} title="Split view">
            <FaExpand /> Split
          </button>
          <button className={`btn btn-sm ${mode === 'preview' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMode('preview')} title="Preview mode">
            <FaEye /> Preview
          </button>
        </div>
        <div className="toolbar-actions">
          {hasChanges && <span className="badge badge-info" style={{ marginRight: 8 }}>Unsaved changes</span>}
          <button className="btn btn-primary btn-sm" onClick={handleSave}
            disabled={savingAction === 'notes' || !hasChanges}>
            {savingAction === 'notes' ? <FaSpinner className="spin" /> : <FaCheck />} Save Notes
          </button>
        </div>
      </div>

      {mode !== 'preview' && (
        <div className="notes-toolbar">
          {NOTES_TOOLBAR.map((btn, i) =>
            btn.sep ? <span key={i} className="notes-toolbar__sep" /> : (
              <button key={i} className="btn-icon" title={btn.title}
                onClick={() => insertMarkdown(btn.before, btn.after, btn.placeholder)}>
                {btn.icon}
              </button>
            )
          )}
        </div>
      )}

      <div className={`notes-body notes-body--${mode}`}>
        {mode !== 'preview' && (
          <div className="notes-editor">
            <textarea
              ref={textareaRef}
              value={notes}
              onChange={e => handleNotesChange(e.target.value)}
              placeholder="Write notes about this router using Markdown...&#10;&#10;## Network Config&#10;- WAN: ether1&#10;- LAN: bridge1&#10;&#10;## Credentials&#10;> Stored in vault&#10;&#10;## Maintenance History&#10;| Date | Action | Notes |&#10;| --- | --- | --- |&#10;| 2024-01-15 | Firmware update | v7.21.3 |"
              spellCheck
            />
          </div>
        )}
        {mode !== 'edit' && (
          <div className="notes-preview">
            {notes ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{notes}</ReactMarkdown>
            ) : (
              <div className="empty">
                <FaStickyNote style={{ fontSize: 48, color: '#ccc', marginBottom: 12 }} />
                <p>No notes yet. Switch to Edit mode to start writing.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// EventLogPanel — live stream of RouterOS event log
// =====================================================================
const EventLogPanel = ({ routerId, showError }) => {
  const apiCall = useApiCall();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [filter, setFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const logEndRef = useRef(null);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    try {
      const data = await apiCall(`/api/RouterConfig/${routerId}/log?count=500`);
      setEntries(data.data || []);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setLoading(false); }
  }, [routerId, apiCall, showError]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(load, 5000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, load]);

  // Auto-scroll to bottom on new entries
  useEffect(() => {
    if (autoRefresh && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entries, autoRefresh]);

  const allTopics = [...new Set(entries.flatMap(e => e.topics.split(',').map(t => t.trim())).filter(Boolean))].sort();

  const filtered = entries.filter(e => {
    if (filter && !e.message?.toLowerCase().includes(filter.toLowerCase()) &&
        !e.topics?.toLowerCase().includes(filter.toLowerCase())) return false;
    if (topicFilter && !e.topics?.includes(topicFilter)) return false;
    return true;
  });

  const topicClass = (topics) => {
    if (topics.includes('error') || topics.includes('critical')) return 'log-error';
    if (topics.includes('warning')) return 'log-warning';
    if (topics.includes('info')) return 'log-info';
    if (topics.includes('system')) return 'log-system';
    return '';
  };

  return (
    <div className="panel">
      <div className="panel-toolbar">
        <input className="search-input" placeholder="Filter log messages..."
          value={filter} onChange={(e) => setFilter(e.target.value)} />
        <div className="toolbar-actions">
          <select className="topic-select" value={topicFilter}
            onChange={e => setTopicFilter(e.target.value)}>
            <option value="">All Topics</option>
            {allTopics.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="toggle-label auto-refresh-toggle">
            <input type="checkbox" checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      <div className="log-container">
        {loading && entries.length === 0 ? (
          <div className="empty">Loading log entries...</div>
        ) : filtered.length === 0 ? (
          <div className="empty">{filter || topicFilter ? 'No log entries match the filter' : 'No log entries found'}</div>
        ) : (
          <div className="log-entries">
            {filtered.map((entry, idx) => (
              <div key={entry.id || idx} className={`log-entry ${topicClass(entry.topics)}`}>
                <span className="log-time mono">{entry.time}</span>
                <span className="log-topics">{entry.topics}</span>
                <span className="log-message">{entry.message}</span>
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// RouterOS command tree for tab completion
// =====================================================================
const ROUTEROS_COMMANDS = [
  '/interface', '/interface/print', '/interface/enable', '/interface/disable', '/interface/set',
  '/interface/bridge', '/interface/bridge/print', '/interface/bridge/add', '/interface/bridge/remove', '/interface/bridge/set',
  '/interface/bridge/port', '/interface/bridge/port/print', '/interface/bridge/port/add', '/interface/bridge/port/remove',
  '/interface/vlan', '/interface/vlan/print', '/interface/vlan/add', '/interface/vlan/remove', '/interface/vlan/set',
  '/interface/ethernet', '/interface/ethernet/print', '/interface/ethernet/set',
  '/interface/wireless', '/interface/wireless/print', '/interface/wireless/set',
  '/interface/list', '/interface/list/print', '/interface/list/member/print',
  '/ip', '/ip/address', '/ip/address/print', '/ip/address/add', '/ip/address/remove', '/ip/address/set',
  '/ip/route', '/ip/route/print', '/ip/route/add', '/ip/route/remove', '/ip/route/set',
  '/ip/firewall', '/ip/firewall/filter', '/ip/firewall/filter/print', '/ip/firewall/filter/add',
  '/ip/firewall/filter/remove', '/ip/firewall/filter/set',
  '/ip/firewall/nat', '/ip/firewall/nat/print', '/ip/firewall/nat/add',
  '/ip/firewall/nat/remove', '/ip/firewall/nat/set',
  '/ip/firewall/mangle', '/ip/firewall/mangle/print',
  '/ip/firewall/address-list', '/ip/firewall/address-list/print', '/ip/firewall/address-list/add',
  '/ip/firewall/connection', '/ip/firewall/connection/print',
  '/ip/dns', '/ip/dns/print', '/ip/dns/set', '/ip/dns/static', '/ip/dns/static/print',
  '/ip/dhcp-server', '/ip/dhcp-server/print', '/ip/dhcp-server/lease', '/ip/dhcp-server/lease/print',
  '/ip/dhcp-client', '/ip/dhcp-client/print',
  '/ip/pool', '/ip/pool/print',
  '/ip/service', '/ip/service/print', '/ip/service/set',
  '/ip/neighbor', '/ip/neighbor/print',
  '/ip/arp', '/ip/arp/print',
  '/system', '/system/resource', '/system/resource/print',
  '/system/identity', '/system/identity/print', '/system/identity/set',
  '/system/clock', '/system/clock/print',
  '/system/routerboard', '/system/routerboard/print',
  '/system/package', '/system/package/print', '/system/package/update',
  '/system/scheduler', '/system/scheduler/print', '/system/scheduler/add',
  '/system/script', '/system/script/print', '/system/script/add', '/system/script/run',
  '/system/logging', '/system/logging/print',
  '/system/ntp/client', '/system/ntp/client/print',
  '/system/health', '/system/health/print',
  '/system/license', '/system/license/print',
  '/system/history', '/system/history/print',
  '/system/note', '/system/note/print',
  '/system/reboot', '/system/shutdown', '/system/reset-configuration',
  '/log', '/log/print',
  '/user', '/user/print', '/user/add', '/user/remove', '/user/set',
  '/user/active', '/user/active/print',
  '/queue', '/queue/simple', '/queue/simple/print', '/queue/simple/add',
  '/queue/tree', '/queue/tree/print',
  '/routing', '/routing/ospf', '/routing/bgp',
  '/ppp', '/ppp/secret', '/ppp/secret/print', '/ppp/active', '/ppp/active/print',
  '/certificate', '/certificate/print',
  '/file', '/file/print',
  '/snmp', '/snmp/print', '/snmp/set',
  '/tool', '/tool/ping', '/tool/traceroute', '/tool/bandwidth-test', '/tool/torch',
  '/export',
];

// =====================================================================
// Command Library — plain-language catalogue of common router tasks.
// Designed so a non-network-engineer can find what they need by intent,
// click it to load the exact RouterOS command, read what it does and
// whether it changes anything, then run it. "Look up" commands are
// read-only and safe; "Change" commands modify configuration.
// {value} placeholders are highlighted so the user knows what to edit.
// =====================================================================
const CLI_COMMAND_LIBRARY = [
  {
    category: 'Status & Health',
    icon: <FaTachometerAlt />,
    commands: [
      { label: 'System resources (CPU, memory, uptime)', command: '/system/resource/print', mutating: false, help: 'Shows CPU load, free memory, disk space, uptime and RouterOS version. The quickest health check.' },
      { label: 'Router identity (name)', command: '/system/identity/print', mutating: false, help: 'Shows the router name. Useful to confirm you are connected to the right device.' },
      { label: 'RouterOS version & board', command: '/system/routerboard/print', mutating: false, help: 'Shows the hardware model, serial number and current/upgrade firmware.' },
      { label: 'Installed packages', command: '/system/package/print', mutating: false, help: 'Lists installed RouterOS packages and whether any are disabled.' },
      { label: 'Health (voltage / temperature)', command: '/system/health/print', mutating: false, help: 'Shows hardware sensors such as voltage and temperature (on supported boards).' },
    ]
  },
  {
    category: 'Connectivity Tests',
    icon: <FaSignal />,
    commands: [
      { label: 'Ping an address (5 times)', command: '/ping address={1.1.1.1} count=5', mutating: false, help: 'Sends 5 pings to test if a host is reachable and how fast it responds. Replace the address with the target you want to test.' },
      { label: 'Trace the route to a host', command: '/tool/traceroute address={8.8.8.8} count=1', mutating: false, help: 'Shows each network hop between this router and the target — useful for finding where traffic stops.' },
      { label: 'Resolve a DNS name', command: '/resolve {google.com}', mutating: false, help: 'Checks that the router can turn a website name into an IP address (confirms DNS is working).' },
    ]
  },
  {
    category: 'Interfaces & IP',
    icon: <FaEthernet />,
    commands: [
      { label: 'List all interfaces', command: '/interface/print', mutating: false, help: 'Shows every interface, whether it is up/down, and traffic counters.' },
      { label: 'List IP addresses', command: '/ip/address/print', mutating: false, help: 'Shows every IP address configured on the router and which interface it belongs to.' },
      { label: 'Ethernet port status & speed', command: '/interface/ethernet/print', mutating: false, help: 'Shows link speed and status for each physical Ethernet port.' },
      { label: 'Enable an interface', command: '/interface/enable {ether2}', mutating: true, help: 'Turns an interface ON. Replace the name with the interface you want to enable.' },
      { label: 'Disable an interface', command: '/interface/disable {ether2}', mutating: true, help: 'Turns an interface OFF. Replace the name with the interface you want to disable. This can cut connectivity — be sure.' },
    ]
  },
  {
    category: 'DHCP & Who Is Connected',
    icon: <FaServer />,
    commands: [
      { label: 'Devices currently connected (DHCP leases)', command: '/ip/dhcp-server/lease/print', mutating: false, help: 'Lists every device that has been given an IP address — name, IP and MAC. The easiest way to see who is on the network.' },
      { label: 'ARP table (live IP ↔ MAC)', command: '/ip/arp/print', mutating: false, help: 'Shows the live mapping of IP addresses to hardware (MAC) addresses currently seen on the network.' },
      { label: 'DHCP servers', command: '/ip/dhcp-server/print', mutating: false, help: 'Shows the configured DHCP servers and which interface each one serves.' },
    ]
  },
  {
    category: 'WiFi (Wireless)',
    icon: <FaWifi />,
    commands: [
      { label: 'List WiFi networks', command: '/interface/wireless/print', mutating: false, help: 'Shows each wireless radio, its network name (SSID), band and whether it is on. Tip: the WiFi tab gives a friendlier point-and-click view of the same thing.' },
      { label: 'Devices connected to WiFi', command: '/interface/wireless/registration-table/print', mutating: false, help: 'Lists devices currently connected over WiFi with their signal strength and link speed.' },
      { label: 'WiFi security profiles', command: '/interface/wireless/security-profiles/print', mutating: false, help: 'Shows the saved WiFi security settings (encryption type and password policy) that networks use.' },
      { label: 'Rename a WiFi network (SSID)', command: '/interface/wireless/set {wlan1} ssid={NewName}', mutating: true, help: 'Changes the broadcast name of a WiFi network. This disconnects connected devices until they reconnect. Replace wlan1 with the radio and NewName with the desired name.' },
    ]
  },
  {
    category: 'Routing & Internet',
    icon: <FaRoute />,
    commands: [
      { label: 'Routing table', command: '/ip/route/print', mutating: false, help: 'Shows all routes, including the default route to the internet and which gateway is active.' },
      { label: 'DHCP client (WAN IP from ISP)', command: '/ip/dhcp-client/print', mutating: false, help: 'Shows IP addresses the router received automatically from an ISP/upstream.' },
      { label: 'DNS settings', command: '/ip/dns/print', mutating: false, help: 'Shows which DNS servers the router uses and whether caching is enabled.' },
    ]
  },
  {
    category: 'Firewall & Security',
    icon: <FaShieldAlt />,
    commands: [
      { label: 'Firewall filter rules', command: '/ip/firewall/filter/print', mutating: false, help: 'Lists the firewall rules that allow or block traffic, in order.' },
      { label: 'NAT rules (port forwards / masquerade)', command: '/ip/firewall/nat/print', mutating: false, help: 'Lists NAT rules, including internet sharing (masquerade) and port forwards.' },
      { label: 'Active connections', command: '/ip/firewall/connection/print', mutating: false, help: 'Shows live connections passing through the router right now.' },
      { label: 'Logged-in users (router accounts)', command: '/user/active/print', mutating: false, help: 'Shows who is currently logged into the router itself.' },
    ]
  },
  {
    category: 'Logs & Backup',
    icon: <FaScroll />,
    commands: [
      { label: 'Recent log entries', command: '/log/print', mutating: false, help: 'Shows the router event log — errors, warnings and notable events.' },
      { label: 'Export full config (read-only)', command: '/export', mutating: false, help: 'Prints the entire router configuration as text. Safe to run — it only reads. Great for reviewing or sharing a config.' },
      { label: 'Files on the router', command: '/file/print', mutating: false, help: 'Lists files stored on the router, including backups.' },
    ]
  },
];

// =====================================================================
// CliTerminalModal — near-fullscreen CLI terminal
// =====================================================================
const CliTerminalModal = ({ routerId, routerConfig, onClose }) => {
  const apiCall = useApiCall();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [command, setCommand] = useState('');
  const [running, setRunning] = useState(false);
  const [cmdHistory, setCmdHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Command Library — guided, plain-language catalogue of common tasks
  const [showLibrary, setShowLibrary] = useState(true);
  const [librarySearch, setLibrarySearch] = useState('');
  const [activeHelp, setActiveHelp] = useState(null);

  /**
   * connectionStatus tracks the actual RouterOS API connection state:
   *   'connecting' — probe in progress on mount
   *   'connected'  — probe succeeded; router is reachable and responsive
   *   'failed'     — probe failed; router is offline or credentials are wrong
   */
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [connectionError, setConnectionError] = useState('');

  const outputRef = useRef(null);
  const inputRef = useRef(null);

  const userLabel = user?.fullName
    || (user?.firstName ? `${user.firstName} ${user?.lastName || ''}`.trim() : null)
    || user?.userEmail || user?.UserEmail
    || 'CapTrac User';

  /**
   * probeConnection — sends a lightweight RouterOS identity command to verify
   * that the router API is reachable and credentials are valid.  Both the
   * initial mount and the manual retry button call this same function.
   */
  const probeConnection = useCallback(async () => {
    setConnectionStatus('connecting');
    setConnectionError('');

    try {
      const data = await apiCall(`/api/RouterConfig/${routerId}/execute-cli`, {
        method: 'POST',
        body: JSON.stringify({ command: '/system/identity/print' })
      });

      if (data.success) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('failed');
        setConnectionError(data.error || 'Router did not respond to connection probe.');
      }
    } catch (err) {
        Sentry.captureException(err);
      setConnectionStatus('failed');
      setConnectionError(err.message || 'Unable to reach router.');
    }
  }, [routerId, apiCall]);

  /**
   * On mount, run the connection probe so that "Connected" is only displayed
   * after the RouterOS API session is confirmed reachable.
   */
  useEffect(() => {
    probeConnection();
  }, [probeConnection]);

  // Auto-focus the input once the connection is confirmed
  useEffect(() => {
    if (connectionStatus === 'connected') {
      inputRef.current?.focus();
    }
  }, [connectionStatus]);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [history]);

  // Tab completion logic
  const getSuggestions = useCallback((text) => {
    if (!text || !text.startsWith('/')) return [];
    const lower = text.toLowerCase();
    return ROUTEROS_COMMANDS
      .filter(c => c.startsWith(lower) && c !== lower)
      .sort((a, b) => a.length - b.length)
      .slice(0, 12);
  }, []);

  const applyCompletion = useCallback((suggestion) => {
    setCommand(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
    inputRef.current?.focus();
  }, []);

  /**
   * loadLibraryCommand — places a library command into the input box so the
   * user can review and edit any {placeholder} values before running it.
   * Placeholders are shown with their braces so they are easy to spot/replace.
   */
  const loadLibraryCommand = useCallback((cmd) => {
    setCommand(cmd.command);
    setShowSuggestions(false);
    inputRef.current?.focus();
  }, []);

  /**
   * Filter the library by the search box. Matches on the friendly label,
   * the underlying command and the help text so intent-based searching works
   * (e.g. "who is connected", "internet", "speed").
   */
  const filteredLibrary = (() => {
    const q = librarySearch.trim().toLowerCase();
    if (!q) return CLI_COMMAND_LIBRARY;
    return CLI_COMMAND_LIBRARY
      .map(group => ({
        ...group,
        commands: group.commands.filter(c =>
          c.label.toLowerCase().includes(q) ||
          c.command.toLowerCase().includes(q) ||
          c.help.toLowerCase().includes(q))
      }))
      .filter(group => group.commands.length > 0);
  })();

  // Format records into structured output history items
  const formatResponse = (data) => {
    const items = [];
    const records = data.records;
    const attrs = data.attributes;
    const output = data.output;

    if (records && records.length > 0) {
      items.push({ type: 'records', records });
    } else if (attrs && Object.keys(attrs).length > 0) {
      // Single record (e.g. /system/resource/print returns one set of attrs)
      items.push({ type: 'records', records: [attrs] });
    } else if (output) {
      items.push({ type: 'output', text: output });
    } else {
      items.push({ type: 'output', text: '(command completed — no output)' });
    }
    return items;
  };

  const executeCommand = async () => {
    const cmd = command.trim();
    if (!cmd) return;

    setShowSuggestions(false);
    setHistory(prev => [...prev, { type: 'input', text: cmd }]);
    setCmdHistory(prev => [cmd, ...prev]);
    setHistoryIdx(-1);
    setCommand('');
    setRunning(true);

    try {
      const data = await apiCall(`/api/RouterConfig/${routerId}/execute-cli`, {
        method: 'POST',
        body: JSON.stringify({ command: cmd })
      });

      if (data.success) {
        const items = formatResponse(data.data || {});
        setHistory(prev => [...prev, ...items]);
      } else {
        setHistory(prev => [...prev, { type: 'error', text: data.error || 'Command failed' }]);
      }
    } catch (e) {
        Sentry.captureException(e);
      setHistory(prev => [...prev, { type: 'error', text: e.message }]);
    }
    finally { setRunning(false); inputRef.current?.focus(); }
  };

  const handleKeyDown = (e) => {
    // Tab completion
    if (e.key === 'Tab') {
      e.preventDefault();
      if (showSuggestions && suggestions.length > 0) {
        applyCompletion(suggestions[selectedSuggestion]);
      } else {
        const matches = getSuggestions(command);
        if (matches.length === 1) {
          applyCompletion(matches[0]);
        } else if (matches.length > 1) {
          // Find longest common prefix
          let prefix = matches[0];
          for (let i = 1; i < matches.length; i++) {
            while (!matches[i].startsWith(prefix)) {
              prefix = prefix.slice(0, -1);
            }
          }
          if (prefix.length > command.length) {
            setCommand(prefix);
          }
          setSuggestions(matches);
          setSelectedSuggestion(0);
          setShowSuggestions(true);
        }
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && suggestions.length > 0) {
        applyCompletion(suggestions[selectedSuggestion]);
      } else {
        executeCommand();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showSuggestions) {
        setSelectedSuggestion(prev => Math.max(0, prev - 1));
      } else if (cmdHistory.length > 0) {
        const newIdx = Math.min(historyIdx + 1, cmdHistory.length - 1);
        setHistoryIdx(newIdx);
        setCommand(cmdHistory[newIdx]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showSuggestions) {
        setSelectedSuggestion(prev => Math.min(suggestions.length - 1, prev + 1));
      } else if (historyIdx > 0) {
        const newIdx = historyIdx - 1;
        setHistoryIdx(newIdx);
        setCommand(cmdHistory[newIdx]);
      } else {
        setHistoryIdx(-1);
        setCommand('');
      }
    } else if (e.key === 'Escape') {
      if (showSuggestions) {
        setShowSuggestions(false);
      } else {
        onClose();
      }
    } else {
      // Close suggestions on other typing
      if (showSuggestions) setShowSuggestions(false);
    }
  };

  // Render a key-value record block
  const renderRecord = (record, idx, total) => {
    const entries = Object.entries(record).filter(([k]) => k !== '.id');
    const idVal = record['.id'];
    return (
      <div key={idx} className="cli-record">
        <div className="cli-record__header">
          {idVal && <span className="cli-record__id">{idVal}</span>}
          {total > 1 && <span className="cli-record__num">#{idx + 1} of {total}</span>}
        </div>
        <div className="cli-record__body">
          {entries.map(([key, val]) => (
            <div key={key} className="cli-kv">
              <span className="cli-kv__key">{key}</span>
              <span className="cli-kv__sep">:</span>
              <span className={`cli-kv__val ${val === 'true' ? 'cli-kv__val--true' : val === 'false' ? 'cli-kv__val--false' : ''}`}>
                {val || <span className="cli-kv__empty">(empty)</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const routerLabel = routerConfig?.displayName || routerConfig?.hostname || `Router ${routerId}`;

  return (
    <div className={`cli-modal-overlay ${isFullscreen ? 'cli-modal--fullscreen' : ''}`} onClick={onClose}>
      <div className="cli-modal" onClick={e => e.stopPropagation()}>
        <div className="cli-modal__header">
          <div className="cli-modal__title">
            <FaTerminal /> Terminal — {routerLabel} ({routerConfig?.ipAddress})
          </div>
          <div className="cli-modal__controls">
            <button className={`btn-icon ${showLibrary ? 'btn-icon--active' : ''}`}
              title={showLibrary ? 'Hide Command Library' : 'Show Command Library'}
              onClick={() => setShowLibrary(!showLibrary)}>
              <FaBook />
            </button>
            <button className="btn-icon" title={isFullscreen ? 'Reduce' : 'Expand'}
              onClick={() => setIsFullscreen(!isFullscreen)}>
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>
            <button className="btn-icon" title="Close (Esc)" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
        </div>
        <div className="cli-modal__main">
        <div className="cli-modal__terminal">
        <div className="cli-modal__body" ref={outputRef}>

          {/* ── Connecting state ── */}
          {connectionStatus === 'connecting' && (
            <div className="cli-welcome cli-welcome--connecting">
              <FaSpinner className="spin" />
              &nbsp; Connecting to {routerLabel} via RouterOS API…
              <br />
              <small className="muted">Verifying session before opening terminal.</small>
            </div>
          )}

          {/* ── Connection failed state ── */}
          {connectionStatus === 'failed' && (
            <div className="cli-welcome cli-welcome--error">
              <span style={{ color: '#f44336', fontWeight: 600 }}>
                ✕ Unable to connect to {routerLabel}
              </span>
              <br />
              <small className="muted" style={{ color: '#f88' }}>
                {connectionError || 'Router is offline or credentials are invalid.'}
              </small>
              <br />
              <button
                className="btn btn-sm btn-secondary"
                style={{ marginTop: '0.75rem' }}
                onClick={probeConnection}
              >
                Retry Connection
              </button>
            </div>
          )}

          {/* ── Connected state ── */}
          {connectionStatus === 'connected' && (
            <>
              <div className="cli-welcome">
                Connected to {routerLabel} via RouterOS API as <strong>{userLabel}</strong>
                <br />
                <small className="muted">
                  Type RouterOS API-style commands (e.g. /system/resource/print, /interface/print).
                  <br />
                  Press <kbd>Tab</kbd> for autocomplete, <kbd>↑↓</kbd> for command history, <kbd>Esc</kbd> to close.
                </small>
              </div>
              {history.map((item, idx) => {
                if (item.type === 'records') {
                  return (
                    <div key={idx} className="cli-records-block">
                      {item.records.map((rec, rIdx) => renderRecord(rec, rIdx, item.records.length))}
                    </div>
                  );
                }
                return (
                  <div key={idx} className={`cli-line cli-line--${item.type}`}>
                    {item.type === 'input' && <span className="cli-prompt">[{userLabel}@{routerLabel}] &gt; </span>}
                    <span className="cli-text">{item.text}</span>
                  </div>
                );
              })}
              {running && (
                <div className="cli-line cli-line--running">
                  <FaSpinner className="spin" /> Executing...
                </div>
              )}
            </>
          )}
        </div>

        {/* Input area is only active when the session is confirmed connected */}
        <div className="cli-modal__input-area">
          {showSuggestions && suggestions.length > 0 && (
            <div className="cli-suggestions">
              {suggestions.map((s, idx) => (
                <div key={s}
                  className={`cli-suggestion ${idx === selectedSuggestion ? 'cli-suggestion--active' : ''}`}
                  onClick={() => applyCompletion(s)}
                  onMouseEnter={() => setSelectedSuggestion(idx)}>
                  {s}
                </div>
              ))}
            </div>
          )}
          <div className="cli-modal__input">
            <span className="cli-prompt">[{userLabel}@{routerLabel}] &gt; </span>
            <input
              ref={inputRef}
              value={command}
              onChange={e => { setCommand(e.target.value); setShowSuggestions(false); }}
              onKeyDown={handleKeyDown}
              /* Disable input while connecting or when connection has failed */
              disabled={running || connectionStatus !== 'connected'}
              placeholder={
                connectionStatus === 'connecting' ? 'Connecting…' :
                connectionStatus === 'failed' ? 'Not connected — retry above' :
                'Enter command… (Tab to autocomplete)'
              }
              spellCheck={false}
              autoComplete="off"
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={executeCommand}
              disabled={running || !command.trim() || connectionStatus !== 'connected'}
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
        </div>{/* /cli-modal__terminal */}

        {showLibrary && (
          <div className="cli-library">
            <div className="cli-library__header">
              <FaBook /> <span>Command Library</span>
            </div>
            <div className="cli-library__intro">
              Not sure what to type? Pick a task below — it loads the exact command into the box so you can review and run it.
            </div>
            <div className="cli-library__search">
              <FaSearch />
              <input
                value={librarySearch}
                onChange={e => setLibrarySearch(e.target.value)}
                placeholder="Search tasks… (e.g. who is connected)"
                spellCheck={false}
              />
            </div>
            <div className="cli-library__list">
              {filteredLibrary.length === 0 && (
                <div className="cli-library__empty">No matching tasks. Try a different word.</div>
              )}
              {filteredLibrary.map(group => (
                <div key={group.category} className="cli-library__group">
                  <div className="cli-library__group-title">{group.icon} {group.category}</div>
                  {group.commands.map(cmd => (
                    <div key={cmd.command} className="cli-lib-item">
                      <div className="cli-lib-item__main">
                        <button
                          className="cli-lib-item__load"
                          disabled={connectionStatus !== 'connected'}
                          onClick={() => loadLibraryCommand(cmd)}
                          title="Load this command into the terminal"
                        >
                          <span className="cli-lib-item__label">{cmd.label}</span>
                          <code className="cli-lib-item__cmd">{cmd.command}</code>
                        </button>
                        <div className="cli-lib-item__meta">
                          <span className={`cli-lib-badge ${cmd.mutating ? 'cli-lib-badge--change' : 'cli-lib-badge--read'}`}>
                            {cmd.mutating ? 'Changes config' : 'Look up'}
                          </span>
                          <button
                            className="cli-lib-item__info"
                            title="What does this do?"
                            onClick={() => setActiveHelp(activeHelp === cmd.command ? null : cmd.command)}
                          >
                            <FaInfoCircle />
                          </button>
                        </div>
                      </div>
                      {activeHelp === cmd.command && (
                        <div className="cli-lib-item__help">{cmd.help}</div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        </div>{/* /cli-modal__main */}
      </div>
    </div>
  );
};

// =====================================================================
// AuditLogPanel — config change audit trail with server-side search and pagination
// =====================================================================
const AuditLogPanel = ({ routerId, showError }) => {
  const apiCall = useApiCall();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // search is the live input value; debouncedSearch drives the API call
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const pageSize = 50;

  // Debounce search input: wait 400 ms after the user stops typing before fetching
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async (p = 1, searchTerm = debouncedSearch) => {
    if (!routerId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, pageSize });
      if (searchTerm) params.set('search', searchTerm);
      const data = await apiCall(`/api/RouterConfig/${routerId}/audit-log?${params}`);
      setEntries(data.data || []);
      setTotal(data.total || 0);
      setPage(p);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setLoading(false); }
  }, [routerId, apiCall, showError, debouncedSearch, pageSize]);

  // Initial load and reload whenever debounced search changes (resets to page 1)
  useEffect(() => {
    (async () => { await load(1, debouncedSearch); })();
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(1, ''); }
  }, [load]);

  const totalPages = Math.ceil(total / pageSize);

  const actionBadgeClass = (action) => {
    if (action === 'Delete' || action === 'Disable' || action === 'RemovePort') return 'badge-danger';
    if (action === 'Create' || action === 'Enable' || action === 'AddPort') return 'badge-success';
    if (action === 'Execute') return 'badge-info';
    return 'badge-type';
  };

  return (
    <div className="panel">
      <div className="panel-toolbar">
        {/* Server-side search — queries the full audit trail, not just the current page */}
        <input
          className="search-input"
          placeholder="Search audit log (action, category, target, user, value)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="toolbar-actions">
          <span className="muted" style={{ fontSize: '0.8rem' }}>
            {total > 0 ? `${total} total entries` : ''}
          </span>
          <button className="btn btn-outline btn-sm" onClick={() => load(1, debouncedSearch)} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      {loading && entries.length === 0 ? (
        <div className="empty">Loading audit log...</div>
      ) : entries.length === 0 ? (
        <div className="empty">{debouncedSearch ? 'No entries match the search' : 'No audit log entries yet'}</div>
      ) : (
        <>
          <table className="data-table audit-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Category</th>
                <th>Action</th>
                <th>Target</th>
                <th>Old Value</th>
                <th>New Value</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} className={!entry.success ? 'row-failed' : ''}>
                  <td className="mono nowrap">{new Date(entry.timestamp).toLocaleString()}</td>
                  <td>{entry.username || <span className="muted">System</span>}</td>
                  <td><span className="badge badge-type">{entry.category}</span></td>
                  <td><span className={`badge ${actionBadgeClass(entry.action)}`}>{entry.action}</span></td>
                  <td className="mono">{entry.target || '—'}</td>
                  <td className="mono muted">{entry.oldValue || '—'}</td>
                  <td className="mono">{entry.newValue || '—'}</td>
                  <td>
                    {entry.success ? (
                      <FaCheckCircle className="toggle-on" title="Success" />
                    ) : (
                      <span title={entry.errorMessage || 'Failed'}>
                        <FaExclamationTriangle className="text-danger" />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="pagination">
              <button className="btn btn-outline btn-xs" disabled={page <= 1} onClick={() => load(page - 1)}>Prev</button>
              <span className="page-info">Page {page} of {totalPages} ({total} entries)</span>
              <button className="btn btn-outline btn-xs" disabled={page >= totalPages} onClick={() => load(page + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// =====================================================================
// VrrpPanel — VRRP interface management with setup guide & sync validation
// =====================================================================
const VrrpPanel = ({ routerId, showSuccess, showError }) => {
  const apiCall = useApiCall();
  const [vrrpInterfaces, setVrrpInterfaces] = useState([]);
  const [interfaces, setInterfaces] = useState([]);
  const [neighbours, setNeighbours] = useState([]);
  const [cluster, setCluster] = useState(null);
  const [syncResults, setSyncResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showClusterCreate, setShowClusterCreate] = useState(false);
  const [showAddBackup, setShowAddBackup] = useState(false);
  const [showSshCredentials, setShowSshCredentials] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [dropTestResult, setDropTestResult] = useState(null);
  const [selectedNeighbourMac, setSelectedNeighbourMac] = useState('');
  const [newVrrp, setNewVrrp] = useState({
    name: 'vrrp1', interface: '', vrid: '1', priority: '100',
    interval: '1s', authPassword: '', comment: ''
  });
  const [clusterForm, setClusterForm] = useState({
    clusterName: '', virtualIpAddress: '', interfaceName: '', vrid: 49, authPassword: ''
  });
  const [backupCredentials, setBackupCredentials] = useState({
    sshUsername: 'admin', sshPassword: ''
  });

  // Load VRRP interfaces, available interfaces, and cluster info
  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    try {
      const [vData, ifData, clusterData] = await Promise.all([
        apiCall(`/api/RouterConfig/${routerId}/vrrp`),
        apiCall(`/api/RouterConfig/${routerId}/interfaces`),
        apiCall(`/api/RouterConfig/${routerId}/cluster`)
      ]);
      setVrrpInterfaces(vData.data || []);
      setInterfaces(ifData.data || []);
      setCluster(clusterData.data || null);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setLoading(false); }
  }, [routerId, apiCall, showError]);

  // Load neighbours list for backup router selection
  const loadNeighbours = useCallback(async () => {
    if (!routerId) return;
    try {
      const nData = await apiCall(`/api/RouterConfig/${routerId}/neighbours`);
      setNeighbours(nData.data || []);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
  }, [routerId, apiCall, showError]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  // ── Create standalone VRRP interface (manual) ──
  const handleCreate = async () => {
    if (!newVrrp.name.trim() || !newVrrp.interface) return;
    setSavingAction('create-vrrp');
    try {
      await apiCall(`/api/RouterConfig/${routerId}/vrrp`, {
        method: 'POST', body: JSON.stringify(newVrrp)
      });
      setShowCreate(false);
      setNewVrrp({ name: 'vrrp1', interface: '', vrid: '1', priority: '100', interval: '1s', authPassword: '', comment: '' });
      showSuccess('VRRP interface created'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  // ── Create VRRP Cluster ──
  const handleCreateCluster = async () => {
    if (!clusterForm.clusterName.trim() || !clusterForm.virtualIpAddress.trim() || !clusterForm.interfaceName) return;
    setSavingAction('create-cluster');
    try {
      const result = await apiCall(`/api/RouterConfig/${routerId}/cluster`, {
        method: 'POST',
        body: JSON.stringify(clusterForm)
      });
      setCluster(result.data);
      setShowClusterCreate(false);
      setClusterForm({ clusterName: '', virtualIpAddress: '', interfaceName: '', vrid: 49, authPassword: '' });
      showSuccess('VRRP cluster created — this router is now the Primary');
      load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  // ── Add backup router to cluster from neighbours ──
  const handleAddBackup = async () => {
    if (!selectedNeighbourMac || !cluster) return;
    setSavingAction('add-backup');
    try {
      const result = await apiCall(`/api/RouterConfig/clusters/${cluster.id}/add-backup`, {
        method: 'POST',
        body: JSON.stringify({
          neighbourMacAddress: selectedNeighbourMac,
          ...(showSshCredentials ? backupCredentials : {})
        })
      });
      setCluster(result.data);
      setShowAddBackup(false);
      setSelectedNeighbourMac('');
      setShowSshCredentials(false);
      setBackupCredentials({ sshUsername: 'admin', sshPassword: '' });
      showSuccess('Backup router added and configured for VRRP failover');
      load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  // ── Refresh cluster status with sync validation ──
  const handleRefreshClusterStatus = async () => {
    if (!cluster) return;
    setSavingAction('refresh-cluster');
    try {
      const result = await apiCall(`/api/RouterConfig/clusters/${cluster.id}/refresh-status`, { method: 'POST' });
      setCluster(result.data);
      // Capture per-member sync validation results if returned
      if (result.syncResults) {
        const syncMap = Object.create(null);
        result.syncResults.forEach(s => { syncMap[s.memberId] = s; });
        setSyncResults(syncMap);
      }
      showSuccess('Cluster status refreshed');
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  // ── Remove cluster member ──
  const handleRemoveClusterMember = async (memberId, routerName) => {
    if (!cluster) return;
    if (!window.confirm(`Remove "${routerName}" from the VRRP cluster? This will also remove its VRRP configuration.`)) return;
    setSavingAction(`rm-member-${memberId}`);
    try {
      await apiCall(`/api/RouterConfig/clusters/${cluster.id}/members/${memberId}`, { method: 'DELETE' });
      showSuccess('Member removed from cluster');
      load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  // ── Delete entire cluster ──
  const handleDeleteCluster = async () => {
    if (!cluster) return;
    if (!window.confirm(`Delete the entire VRRP cluster "${cluster.name}"? This will remove VRRP configuration from all member routers.`)) return;
    setSavingAction('delete-cluster');
    try {
      await apiCall(`/api/RouterConfig/clusters/${cluster.id}`, { method: 'DELETE' });
      setCluster(null);
      showSuccess('VRRP cluster deleted');
      load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  // ── Perform drop test ──
  const handleDropTest = async () => {
    if (!cluster) return;
    if (!window.confirm(
      `Run a failover drop test on "${cluster.name}"?\n\n` +
      'This will temporarily disable VRRP on the primary router to verify the backup takes over, ' +
      'then re-enable and verify recovery. The test auto-reverts after completion.\n\n' +
      'A detailed email report will be sent to your account email.'
    )) return;
    setSavingAction('drop-test');
    setDropTestResult(null);
    try {
      const result = await apiCall(`/api/RouterConfig/clusters/${cluster.id}/drop-test`, { method: 'POST' });
      setDropTestResult(result.data);
      if (result.emailSent) {
        showSuccess(`Drop test complete — ${result.data.overallResult}. Email report sent.`);
      } else {
        showSuccess(`Drop test complete — ${result.data.overallResult}. No email sent (no email configured).`);
      }
      load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleRemove = async (vrrpId, name) => {
    if (!window.confirm(`Remove VRRP interface "${name}"?`)) return;
    setSavingAction(`rm-${vrrpId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/vrrp/${encodeURIComponent(vrrpId)}`, { method: 'DELETE' });
      showSuccess('VRRP interface removed'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  const handleToggleVrrp = async (vrrpId, isDisabled) => {
    setSavingAction(`toggle-${vrrpId}`);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/vrrp/${encodeURIComponent(vrrpId)}/toggle`, {
        method: 'PUT', body: JSON.stringify({ disabled: !isDisabled })
      });
      showSuccess(isDisabled ? 'VRRP interface enabled' : 'VRRP interface disabled'); load();
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setSavingAction(null); }
  };

  // Helper for cluster status badge colour
  const getClusterStatusBadge = (status) => {
    switch (status) {
      case 'Active': return 'badge-success';
      case 'Degraded': return 'badge-warning';
      case 'Offline': return 'badge-danger';
      case 'Provisioning': return 'badge-info';
      default: return 'badge-muted';
    }
  };

  // Helper for member role badge colour
  const getRoleBadge = (role) => {
    switch (role) {
      case 'Primary': return 'badge-primary';
      case 'Secondary': return 'badge-info';
      case 'Tertiary': return 'badge-warning';
      default: return 'badge-muted';
    }
  };

  // Helper: compute setup checklist status from cluster data
  const getSetupChecklist = () => {
    if (!cluster) return [];
    const members = cluster.members || [];
    const primaryMember = members.find(m => m.role === 'Primary');
    const backupMembers = members.filter(m => m.role !== 'Primary');
    const allConfigured = members.every(m => m.provisioningStatus === 'Configured');
    const hasMaster = members.some(m => m.isCurrentMaster);
    const hasBackup = backupMembers.length > 0;
    const allSynced = !syncResults || Object.values(syncResults).every(s => s.isSynced);
    const allPortsConnected = syncResults ? Object.values(syncResults).every(s => s.portConnected) : false;

    return [
      {
        label: 'Primary router configured',
        detail: primaryMember
          ? `${primaryMember.routerName || primaryMember.routerIp} (priority ${primaryMember.priority})`
          : 'Not set',
        done: !!primaryMember && primaryMember.provisioningStatus === 'Configured',
        userAction: false
      },
      {
        label: 'Backup router connected & cabled',
        detail: hasBackup
          ? `${backupMembers.length} backup router${backupMembers.length > 1 ? 's' : ''} added`
          : 'Connect a backup router to the same switch/network, then click "Add Backup Router"',
        done: hasBackup,
        userAction: !hasBackup
      },
      {
        label: 'Backup router(s) provisioned',
        detail: hasBackup
          ? backupMembers.every(m => m.provisioningStatus === 'Configured')
            ? 'All backup routers configured successfully'
            : backupMembers.some(m => m.provisioningStatus === 'Failed')
              ? 'One or more backup routers failed provisioning — check errors below'
              : 'Provisioning in progress...'
          : 'Waiting for backup router to be added',
        done: hasBackup && backupMembers.every(m => m.provisioningStatus === 'Configured'),
        userAction: false
      },
      {
        label: 'All ports connected (cables plugged in)',
        detail: (() => {
          if (!syncResults) return 'Click "Refresh Status" to verify port connectivity';
          if (allPortsConnected) return `All members have link on ${cluster.interfaceName}`;
          const disconnected = Object.values(syncResults).filter(s => !s.portConnected);
          const disconnectedNames = disconnected.map(s => {
            const member = members.find(m => m.id === s.memberId);
            return member?.routerName || member?.routerIp || `Member ${s.memberId}`;
          });
          return `No link detected on: ${disconnectedNames.join(', ')} — check cable connections`;
        })(),
        done: !!syncResults && allPortsConnected,
        userAction: syncResults && !allPortsConnected
      },
      {
        label: 'VRRP failover active',
        detail: hasMaster
          ? `Master elected — failover is operational`
          : allConfigured && hasBackup
            ? 'Waiting for VRRP master election — click "Refresh Status"'
            : 'Complete steps above first',
        done: hasMaster,
        userAction: false
      },
      {
        label: 'Configuration synced across all members',
        detail: (() => {
          if (!syncResults) return 'Click "Refresh Status" to verify sync';
          if (allSynced) {
            const syncDate = cluster.lastSyncDate
              ? new Date(cluster.lastSyncDate).toLocaleString()
              : 'just now';
            return `Last verified: ${syncDate}`;
          }
          return 'Config mismatches detected — see member details below';
        })(),
        done: !!syncResults && allSynced,
        userAction: !syncResults
      },
      {
        label: 'Drop test verified',
        detail: dropTestResult
          ? dropTestResult.overallResult === 'Passed'
            ? `Passed — tested ${new Date(dropTestResult.startedAt).toLocaleString()}`
            : `${dropTestResult.overallResult} — see results below for details`
          : 'Run a drop test to verify failover works end-to-end',
        done: dropTestResult?.overallResult === 'Passed',
        userAction: !dropTestResult
      }
    ];
  };

  return (
    <div className="panel">
      {/* ── VRRP Cluster Section ── */}
      <div className="panel-toolbar">
        <span className="panel-section-title">
          <FaProjectDiagram style={{ marginRight: 6 }} /> VRRP Cluster
        </span>
        <div className="toolbar-actions">
          {!cluster && (
            <>
              <button className="btn btn-outline btn-sm" onClick={() => setShowSetupGuide(!showSetupGuide)}>
                <FaScroll /> Setup Guide
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => {
                setShowClusterCreate(!showClusterCreate);
                if (!showClusterCreate && interfaces.length === 0) load();
              }}>
                <FaPlus /> Create Cluster
              </button>
            </>
          )}
          {cluster && (
            <>
              <button className="btn btn-outline btn-sm" onClick={() => setShowSetupGuide(!showSetupGuide)}>
                <FaScroll /> {showSetupGuide ? 'Hide Guide' : 'Setup Guide'}
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => {
                setShowAddBackup(!showAddBackup);
                if (!showAddBackup) loadNeighbours();
              }}>
                <FaPlus /> Add Backup Router
              </button>
              <button className="btn btn-outline btn-sm" onClick={handleRefreshClusterStatus}
                disabled={savingAction === 'refresh-cluster'}>
                {savingAction === 'refresh-cluster' ? <FaSpinner className="spin" /> : <FaSync />} Refresh Status
              </button>
              <button className="btn btn-outline btn-sm" onClick={handleDropTest}
                disabled={!!savingAction || (cluster.members || []).length < 2}
                title={(cluster.members || []).length < 2
                  ? 'Add at least one backup router before running a drop test'
                  : 'Simulate primary failure to test failover — auto-reverts after test'}>
                {savingAction === 'drop-test' ? <FaSpinner className="spin" /> : <FaExchangeAlt />} Drop Test
              </button>
              <button className="btn btn-outline btn-sm btn-danger" onClick={handleDeleteCluster}
                disabled={savingAction === 'delete-cluster'} title="Delete entire cluster">
                {savingAction === 'delete-cluster' ? <FaSpinner className="spin" /> : <FaTrash />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Physical Setup Guide ── */}
      {showSetupGuide && (
        <div className="add-form" style={{ marginBottom: 16, background: '#f8f9ff', borderLeft: '4px solid #4361ee' }}>
          <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaScroll style={{ color: '#4361ee' }} /> VRRP Failover Setup Guide — MikroTik RouterOS 7
          </div>

          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <p style={{ fontWeight: 600, margin: '0 0 6px', color: '#333' }}>
              What You Need:
            </p>
            <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
              <li><strong>Primary Router</strong> — already managed by CapTrac (this router)</li>
              <li><strong>Backup Router</strong> — a factory-reset or blank MikroTik router</li>
              <li><strong>Ethernet Cable</strong> — to connect the backup router to your network</li>
              <li><strong>Network Switch</strong> — both routers must be on the same Layer 2 segment</li>
            </ul>

            <p style={{ fontWeight: 600, margin: '0 0 6px', color: '#333' }}>
              Step 1 — Physical Cabling (You Do This):
            </p>
            <div style={{
              background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: 12,
              marginBottom: 12, fontSize: 13
            }}>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li>
                  <strong>Connect the backup router's <code>ether1</code> port</strong> to the <strong>same
                  switch or network segment</strong> that the primary router's VRRP interface is connected to.
                  <br />
                  <span className="muted">Both routers must be able to reach each other directly on the
                  same VLAN / broadcast domain for VRRP advertisements to work.</span>
                </li>
                <li style={{ marginTop: 6 }}>
                  <strong>Power on the backup router</strong> and wait ~60 seconds for it to boot fully.
                </li>
                <li style={{ marginTop: 6 }}>
                  <strong>Do NOT configure anything manually</strong> on the backup router. CapTrac will
                  SSH into it, reset its configuration, and set up VRRP automatically.
                </li>
              </ol>
            </div>

            <p style={{ fontWeight: 600, margin: '0 0 6px', color: '#333' }}>
              Step 2 — Create a Cluster (System Handles This):
            </p>
            <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
              <li>Click <strong>"Create Cluster"</strong> above and fill in the cluster name, virtual IP, interface, and VRID.</li>
              <li>CapTrac configures a VRRP interface on the primary router with <strong>priority 254</strong> (highest).</li>
              <li>The <strong>Virtual IP</strong> is the shared gateway address — all client devices should use this IP as their default gateway.</li>
            </ul>

            <p style={{ fontWeight: 600, margin: '0 0 6px', color: '#333' }}>
              Step 3 — Add Backup Router (System Handles This):
            </p>
            <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
              <li>Click <strong>"Add Backup Router"</strong> — CapTrac discovers the backup via the neighbour table.</li>
              <li>Select the backup router by its <strong>MAC address</strong>.</li>
              <li>CapTrac will:
                <ol style={{ paddingLeft: 20, marginTop: 4 }}>
                  <li>SSH into the backup router (default: <code>admin</code> / no password)</li>
                  <li>Reset its config to a blank slate (login user is preserved)</li>
                  <li>Wait for reboot (~60–120 seconds)</li>
                  <li>Configure VRRP with a lower priority (e.g. 200 for secondary)</li>
                  <li>Assign the virtual IP and enable the API service</li>
                </ol>
              </li>
              <li>If the default admin login doesn't work, tick <strong>"Specify SSH credentials"</strong>.</li>
            </ul>

            <p style={{ fontWeight: 600, margin: '0 0 6px', color: '#333' }}>
              Step 4 — Verify Failover (You Confirm This):
            </p>
            <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
              <li>Click <strong>"Refresh Status"</strong> to verify both routers show their VRRP state.</li>
              <li>The primary should show <strong>"Master"</strong> and the backup should show <strong>"Backup"</strong>.</li>
              <li>To test failover: unplug or power off the primary router — the backup should become master within ~3 seconds.</li>
              <li>Re-connect the primary — it will automatically reclaim the master role (preemption is enabled).</li>
            </ul>

            <div style={{
              background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6,
              padding: 10, fontSize: 12, display: 'flex', alignItems: 'flex-start', gap: 8
            }}>
              <FaExclamationTriangle style={{ color: '#856404', flexShrink: 0, marginTop: 2 }} />
              <div>
                <strong>Important Notes:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                  <li>Both routers <strong>must use the same interface name</strong> (e.g. <code>ether1</code>) for VRRP.</li>
                  <li>The VRID (Virtual Router ID) must be unique per network segment — use a different VRID if you have multiple VRRP groups.</li>
                  <li><strong>VRRPv3</strong> (default) does not support authentication per RFC 5798. If you set an auth password, the system uses <strong>VRRPv2</strong> with AH authentication instead.</li>
                  <li>Client devices must use the <strong>Virtual IP</strong> as their gateway — not the physical IP of either router.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Setup Checklist (shown when cluster exists) ── */}
      {cluster && (
        <div style={{
          marginBottom: 16, background: '#fafbfc', border: '1px solid #e1e4e8',
          borderRadius: 6, padding: 14
        }}>
          <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaShieldAlt style={{ color: '#4361ee' }} /> Setup Checklist
          </div>
          {getSetupChecklist().map((step, index) => (
            <div key={index} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0',
              borderBottom: index < getSetupChecklist().length - 1 ? '1px solid #eee' : 'none'
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, marginTop: 1,
                background: step.done ? '#e7f5e7' : step.userAction ? '#fff8e1' : '#f5f5f5',
                color: step.done ? '#4caf50' : step.userAction ? '#ff9800' : '#999',
                border: `1px solid ${step.done ? '#4caf50' : step.userAction ? '#ff9800' : '#ddd'}`
              }}>
                {step.done ? <FaCheck style={{ fontSize: 10 }} /> :
                  step.userAction ? '!' : (index + 1)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 13, color: step.done ? '#333' : '#666' }}>
                  {step.label}
                  {step.userAction && !step.done && (
                    <span style={{
                      marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 3,
                      background: '#fff8e1', color: '#e65100', border: '1px solid #ffcc80'
                    }}>Your Action Required</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{step.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create Cluster Form ── */}
      {showClusterCreate && !cluster && (
        <div className="add-form">
          <div className="form-row">
            <div className="form-group">
              <label>Cluster Name</label>
              <input placeholder="e.g. Mall-A Primary Cluster" value={clusterForm.clusterName}
                onChange={e => setClusterForm(p => ({ ...p, clusterName: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Virtual IP (CIDR)</label>
              <input placeholder="e.g. 192.168.1.1/24" value={clusterForm.virtualIpAddress}
                onChange={e => setClusterForm(p => ({ ...p, virtualIpAddress: e.target.value }))} />
              <span className="muted" style={{ fontSize: 11 }}>
                This IP will be the shared gateway. Clients should use this as their default gateway.
              </span>
            </div>
            <div className="form-group">
              <label>Interface</label>
              <InterfaceStatusSelect
                value={clusterForm.interfaceName}
                onChange={e => setClusterForm(p => ({ ...p, interfaceName: e.target.value }))}
                interfaces={interfaces}
              />
              <span className="muted" style={{ fontSize: 11 }}>
                All cluster members must use the same interface name (e.g. ether1).
              </span>
            </div>
          </div>
          <div className="form-row" style={{ marginTop: 8 }}>
            <div className="form-group">
              <label>VRID</label>
              <input type="number" placeholder="49" value={clusterForm.vrid}
                onChange={e => setClusterForm(p => ({ ...p, vrid: parseInt(e.target.value) || 49 }))} />
              <span className="muted" style={{ fontSize: 11 }}>
                Must be unique per network segment (1–255).
              </span>
            </div>
            <div className="form-group">
              <label>Auth Password <span className="muted" style={{ fontWeight: 400 }}>(optional — uses VRRPv2)</span></label>
              <input type="password" placeholder="Leave blank for VRRPv3 (recommended)" value={clusterForm.authPassword}
                onChange={e => setClusterForm(p => ({ ...p, authPassword: e.target.value }))} />
              <span className="muted" style={{ fontSize: 11 }}>
                {clusterForm.authPassword
                  ? '⚠ Setting a password switches from VRRPv3 to VRRPv2 (AH authentication).'
                  : 'VRRPv3 (default) — recommended per RFC 5798. No authentication.'}
              </span>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary btn-sm" onClick={handleCreateCluster}
                disabled={savingAction === 'create-cluster' || !clusterForm.clusterName.trim() || !clusterForm.virtualIpAddress.trim() || !clusterForm.interfaceName}>
                {savingAction === 'create-cluster' ? <FaSpinner className="spin" /> : <FaCheck />} Create Cluster
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => setShowClusterCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cluster Info & Members ── */}
      {cluster && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{cluster.name}</span>
            <span className={`badge ${getClusterStatusBadge(cluster.status)}`}>{cluster.status}</span>
            <span className="muted" style={{ fontSize: 12 }}>VRID: {cluster.vrid}</span>
            <span className="muted" style={{ fontSize: 12 }}>VIP: {cluster.virtualIpAddress}</span>
            <span className="muted" style={{ fontSize: 12 }}>Interface: {cluster.interfaceName}</span>
            <span className="muted" style={{ fontSize: 12 }}>
              {cluster.usesAuthentication ? 'VRRPv2 (AH Auth)' : 'VRRPv3'}
            </span>
            {cluster.lastSyncDate && (
              <span className="muted" style={{ fontSize: 11 }}>
                Last sync: {new Date(cluster.lastSyncDate).toLocaleString()}
              </span>
            )}
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Router</th>
                <th>IP Address</th>
                <th>Priority</th>
                <th>VRRP State</th>
                <th>Status</th>
                <th>Port</th>
                <th>Sync</th>
                <th>Last Check</th>
                <th className="col-center col-action" style={{ width: 50 }}>Remove</th>
              </tr>
            </thead>
            <tbody>
              {(cluster.members || []).map(m => {
                const memberSync = syncResults?.[m.id];
                return (
                  <tr key={m.id}>
                    <td>
                      <span className={`badge ${getRoleBadge(m.role)}`}>
                        {m.role}
                      </span>
                    </td>
                    <td className="fw">{m.routerName || '—'}</td>
                    <td className="mono">{m.routerIp || '—'}</td>
                    <td className="mono">{m.priority}</td>
                    <td>
                      {m.isCurrentMaster ? (
                        <span className="badge badge-success"><FaCheckCircle style={{ marginRight: 4 }} /> Master</span>
                      ) : (
                        <span className="badge badge-info">Backup</span>
                      )}
                    </td>
                    <td>
                      {m.provisioningStatus === 'Configured' && m.isOnline ? (
                        <span className="badge badge-success">Online</span>
                      ) : m.provisioningStatus === 'Provisioning' ? (
                        <span className="badge badge-info"><FaSpinner className="spin" style={{ marginRight: 4 }} /> Provisioning</span>
                      ) : m.provisioningStatus === 'Failed' ? (
                        <span className="badge badge-danger" title={m.lastProvisioningError || ''}>
                          <FaExclamationTriangle style={{ marginRight: 4 }} /> Failed
                        </span>
                      ) : (
                        <span className="badge badge-muted">Offline</span>
                      )}
                    </td>
                    <td>
                      {memberSync ? (
                        memberSync.portConnected ? (
                          <span className="badge badge-success" title={`${cluster.interfaceName} has link`}>
                            <FaLink style={{ marginRight: 4 }} /> Connected
                          </span>
                        ) : (
                          <span className="badge badge-danger" title={`${cluster.interfaceName} has no link — check cable`}>
                            <FaUnlink style={{ marginRight: 4 }} /> No Link
                          </span>
                        )
                      ) : (
                        <span className="badge badge-muted" title="Click Refresh Status to check port">—</span>
                      )}
                    </td>
                    <td>
                      {memberSync ? (
                        memberSync.isSynced ? (
                          <span className="badge badge-success" title="Configuration matches cluster settings">
                            <FaCheckCircle style={{ marginRight: 4 }} /> Synced
                          </span>
                        ) : (() => {
                          const issueCount = memberSync.issues?.length || 0;
                          return (
                            <span className="badge badge-warning"
                              title={memberSync.issues?.join('\n') || 'Config mismatch'}>
                              <FaExclamationTriangle style={{ marginRight: 4 }} />
                              {issueCount} issue{issueCount !== 1 ? 's' : ''}
                            </span>
                          );
                        })()
                      ) : (
                        <span className="badge badge-muted" title="Click Refresh Status to check sync">—</span>
                      )}
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {m.lastStatusCheck ? new Date(m.lastStatusCheck).toLocaleString() : '—'}
                    </td>
                    <td className="col-center col-action">
                      <button className="btn-icon btn-danger" title="Remove from cluster"
                        onClick={() => handleRemoveClusterMember(m.id, m.routerName || m.routerIp)}
                        disabled={!!savingAction}>
                        {savingAction === `rm-member-${m.id}` ? <FaSpinner className="spin" /> : <FaTrash />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── Sync Issue Details (shown when mismatches exist) ── */}
          {syncResults && Object.values(syncResults).some(s => !s.isSynced) && (
            <div style={{
              marginTop: 10, background: '#fff8e1', border: '1px solid #ffcc80',
              borderRadius: 6, padding: 10, fontSize: 12
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FaExclamationTriangle style={{ color: '#e65100' }} /> Configuration Sync Issues
              </div>
              {Object.values(syncResults).filter(s => !s.isSynced).map(s => {
                const member = (cluster.members || []).find(m => m.id === s.memberId);
                return (
                  <div key={s.memberId} style={{ marginBottom: 4 }}>
                    <strong>{member?.routerName || member?.routerIp || `Member ${s.memberId}`}:</strong>
                    <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                      {(s.issues || []).map((issue, i) => <li key={i}>{issue}</li>)}
                    </ul>
                  </div>
                );
              })}
              <div className="muted" style={{ marginTop: 6 }}>
                To resolve: try removing and re-adding the affected member, or manually fix the VRRP config via the router's CLI.
              </div>
            </div>
          )}

          {/* ── Drop Test Progress ── */}
          {savingAction === 'drop-test' && (
            <div style={{
              marginTop: 10, background: '#e3f2fd', border: '1px solid #90caf9',
              borderRadius: 6, padding: 14, fontSize: 13
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FaSpinner className="spin" style={{ color: '#1565c0' }} /> Drop Test in Progress
              </div>
              <p className="muted" style={{ margin: 0 }}>
                The system is simulating a primary failure by temporarily disabling VRRP on the master router,
                verifying backup promotion, then re-enabling and confirming recovery. This takes approximately 30 seconds.
              </p>
            </div>
          )}

          {/* ── Drop Test Results ── */}
          {dropTestResult && (
            <div style={{
              marginTop: 10,
              background: dropTestResult.overallResult === 'Passed' ? '#e8f5e9'
                : dropTestResult.overallResult === 'Partial' ? '#fff8e1'
                : '#ffebee',
              border: `1px solid ${dropTestResult.overallResult === 'Passed' ? '#81c784'
                : dropTestResult.overallResult === 'Partial' ? '#ffcc80'
                : '#ef9a9a'}`,
              borderRadius: 6, padding: 14, fontSize: 13
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {dropTestResult.overallResult === 'Passed' ? (
                    <FaCheckCircle style={{ color: '#4caf50' }} />
                  ) : dropTestResult.overallResult === 'Partial' ? (
                    <FaExclamationTriangle style={{ color: '#ff9800' }} />
                  ) : (
                    <FaExclamationTriangle style={{ color: '#f44336' }} />
                  )}
                  Drop Test Result: {dropTestResult.overallResult}
                </div>
                <button className="btn btn-outline btn-sm" onClick={() => setDropTestResult(null)}
                  style={{ fontSize: 11 }}>
                  <FaTimes /> Dismiss
                </button>
              </div>

              <p style={{ margin: '0 0 10px', color: '#555' }}>{dropTestResult.summary}</p>

              <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                <strong>Test performed by:</strong> {dropTestResult.performedBy} &nbsp;|&nbsp;
                <strong>Duration:</strong> {dropTestResult.completedAt && dropTestResult.startedAt
                  ? `${((new Date(dropTestResult.completedAt) - new Date(dropTestResult.startedAt)) / 1000).toFixed(1)}s`
                  : '—'} &nbsp;|&nbsp;
                <strong>Original master:</strong> {dropTestResult.originalMasterName}
                {dropTestResult.failoverMasterName && (
                  <> &nbsp;|&nbsp; <strong>Failover master:</strong> {dropTestResult.failoverMasterName}</>
                )}
              </div>

              {/* Phase details */}
              <table className="data-table" style={{ marginBottom: 10 }}>
                <thead>
                  <tr>
                    <th>Phase</th>
                    <th>Result</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {(dropTestResult.phases || []).map((phase, i) => (
                    <tr key={i}>
                      <td className="fw">{phase.name}</td>
                      <td>
                        {phase.success ? (
                          <span className="badge badge-success"><FaCheckCircle style={{ marginRight: 4 }} /> Pass</span>
                        ) : (
                          <span className="badge badge-danger"><FaExclamationTriangle style={{ marginRight: 4 }} /> Fail</span>
                        )}
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>{phase.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Member state tracking */}
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Router</th>
                    <th>Role</th>
                    <th>Pre-Test</th>
                    <th>Failover</th>
                    <th>Post-Recovery</th>
                    <th>Port</th>
                    <th>Sync</th>
                  </tr>
                </thead>
                <tbody>
                  {(dropTestResult.memberResults || []).map(m => (
                    <tr key={m.memberId}>
                      <td>
                        <div className="fw">{m.routerName}</div>
                        <div className="mono muted" style={{ fontSize: 11 }}>{m.routerIp}</div>
                      </td>
                      <td><span className={`badge ${getRoleBadge(m.role)}`}>{m.role}</span></td>
                      <td>
                        <span className={`badge ${m.preTestState === 'Master' ? 'badge-success' : 'badge-info'}`}>
                          {m.preTestState}
                        </span>
                      </td>
                      <td>
                        {m.failoverState ? (
                          <span className={`badge ${m.failoverState === 'Master' ? 'badge-success' : 'badge-info'}`}>
                            {m.failoverState}
                          </span>
                        ) : <span className="badge badge-muted">—</span>}
                      </td>
                      <td>
                        {m.postTestState ? (
                          <span className={`badge ${m.postTestState === 'Master' ? 'badge-success' : 'badge-info'}`}>
                            {m.postTestState}
                          </span>
                        ) : <span className="badge badge-muted">—</span>}
                      </td>
                      <td>
                        {m.postTestPortConnected ? (
                          <span className="badge badge-success"><FaLink style={{ marginRight: 3 }} /> OK</span>
                        ) : (
                          <span className="badge badge-danger"><FaUnlink style={{ marginRight: 3 }} /> No Link</span>
                        )}
                      </td>
                      <td>
                        {m.postTestSynced ? (
                          <span className="badge badge-success"><FaCheckCircle style={{ marginRight: 3 }} /> OK</span>
                        ) : (
                          <span className="badge badge-warning" title={(m.postTestIssues || []).join('\n')}>
                            <FaExclamationTriangle style={{ marginRight: 3 }} /> Issues
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Add Backup Router Form (select from neighbours) ── */}
      {showAddBackup && cluster && (
        <div className="add-form" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            <FaSitemap style={{ marginRight: 6 }} /> Select Neighbour to Add as Backup
          </div>
          <p className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
            Select a router from the neighbour discovery table below. The selected router will be
            reset to a blank configuration (keeping login credentials) and automatically configured
            as a VRRP backup member of this cluster.
          </p>

          {neighbours.length === 0 ? (
            <div className="empty">
              <FaSpinner className="spin" /> Discovering neighbours...
              <button className="btn btn-outline btn-sm" style={{ marginLeft: 8 }} onClick={loadNeighbours}>
                <FaSync /> Retry
              </button>
            </div>
          ) : (
            <table className="data-table" style={{ marginBottom: 8 }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>Select</th>
                  <th>Identity</th>
                  <th>IP Address</th>
                  <th>MAC Address</th>
                  <th>Interface</th>
                  <th>Platform</th>
                </tr>
              </thead>
              <tbody>
                {neighbours.map(n => (
                  <tr key={n.macAddress || n.id}
                    style={{
                      cursor: 'pointer',
                      background: selectedNeighbourMac === n.macAddress ? 'rgba(0,123,255,0.08)' : undefined
                    }}
                    onClick={() => setSelectedNeighbourMac(n.macAddress)}>
                    <td className="col-center">
                      <input type="radio" name="neighbourSelect"
                        checked={selectedNeighbourMac === n.macAddress}
                        onChange={() => setSelectedNeighbourMac(n.macAddress)} />
                    </td>
                    <td className="fw">{n.identity || '—'}</td>
                    <td className="mono">{n.address || '—'}</td>
                    <td className="mono">{n.macAddress}</td>
                    <td>{n.interface || n.interfaceName || '—'}</td>
                    <td>{n.platform || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Optional SSH credentials (shown if default admin/blank fails) */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={showSshCredentials}
                onChange={e => setShowSshCredentials(e.target.checked)}
                style={{ marginRight: 6 }} />
              Specify SSH credentials (if default admin login fails)
            </label>
          </div>

          {showSshCredentials && (
            <div className="form-row" style={{ marginBottom: 8 }}>
              <div className="form-group">
                <label>SSH Username</label>
                <input value={backupCredentials.sshUsername}
                  onChange={e => setBackupCredentials(p => ({ ...p, sshUsername: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>SSH Password</label>
                <input type="password" value={backupCredentials.sshPassword}
                  onChange={e => setBackupCredentials(p => ({ ...p, sshPassword: e.target.value }))} />
              </div>
            </div>
          )}

          <div className="form-actions">
            <button className="btn btn-primary btn-sm" onClick={handleAddBackup}
              disabled={!selectedNeighbourMac || savingAction === 'add-backup'}>
              {savingAction === 'add-backup' ? (
                <><FaSpinner className="spin" /> Configuring Backup Router...</>
              ) : (
                <><FaCheck /> Add as Backup Router</>
              )}
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => {
              setShowAddBackup(false);
              setSelectedNeighbourMac('');
              setShowSshCredentials(false);
            }}>Cancel</button>
          </div>

          {savingAction === 'add-backup' && (
            <div className="muted" style={{ fontSize: 12, marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaSpinner className="spin" />
              This may take 2-3 minutes. The backup router is being reset, rebooted, and configured for VRRP.
            </div>
          )}
        </div>
      )}

      <hr style={{ margin: '16px 0', borderColor: '#eee' }} />

      {/* ── Standalone VRRP Interfaces Section (manual) ── */}
      <div className="panel-toolbar">
        <span className="panel-section-title">VRRP Interfaces (Manual)</span>
        <div className="toolbar-actions">
          <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(!showCreate)}>
            <FaPlus /> New VRRP
          </button>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="add-form">
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <input placeholder="vrrp1" value={newVrrp.name}
                onChange={e => setNewVrrp(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Interface</label>
              <InterfaceStatusSelect
                value={newVrrp.interface}
                onChange={e => setNewVrrp(p => ({ ...p, interface: e.target.value }))}
                interfaces={interfaces}
              />
            </div>
            <div className="form-group">
              <label>VRID</label>
              <input type="number" placeholder="1" value={newVrrp.vrid}
                onChange={e => setNewVrrp(p => ({ ...p, vrid: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Priority</label>
              <input type="number" placeholder="100" value={newVrrp.priority}
                onChange={e => setNewVrrp(p => ({ ...p, priority: e.target.value }))} />
            </div>
          </div>
          <div className="form-row" style={{ marginTop: 8 }}>
            <div className="form-group">
              <label>Interval</label>
              <input placeholder="1s" value={newVrrp.interval}
                onChange={e => setNewVrrp(p => ({ ...p, interval: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Auth Password</label>
              <input type="password" placeholder="Optional" value={newVrrp.authPassword}
                onChange={e => setNewVrrp(p => ({ ...p, authPassword: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Comment</label>
              <input placeholder="Optional" value={newVrrp.comment}
                onChange={e => setNewVrrp(p => ({ ...p, comment: e.target.value }))} />
            </div>
            <div className="form-actions">
              <button className="btn btn-primary btn-sm" onClick={handleCreate}
                disabled={savingAction === 'create-vrrp'}>
                {savingAction === 'create-vrrp' ? <FaSpinner className="spin" /> : <FaCheck />} Create
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading && vrrpInterfaces.length === 0 ? (
        <div className="empty">Loading VRRP interfaces...</div>
      ) : vrrpInterfaces.length === 0 ? (
        <div className="empty">No VRRP interfaces configured</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th><th>Interface</th><th>VRID</th><th>Priority</th>
              <th>Interval</th><th>Role</th><th>Status</th><th>Comment</th>
              <th className="col-center col-action" style={{ width: 50 }}>Enable</th>
              <th className="col-center col-action" style={{ width: 50 }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {vrrpInterfaces.map(v => (
              <tr key={v.id} className={v.disabled ? 'row-disabled' : ''}>
                <td className="fw">{v.name}</td>
                <td>{v.interface}</td>
                <td className="mono">{v.vrid}</td>
                <td className="mono">{v.priority}</td>
                <td className="mono">{v.interval}</td>
                <td>
                  <span className={`badge ${v.isMaster ? 'badge-success' : 'badge-info'}`}>
                    {v.isMaster ? 'Master' : 'Backup'}
                  </span>
                </td>
                <td>
                  {v.running ? <span className="badge badge-success">Running</span> :
                    v.disabled ? <span className="badge badge-muted">Disabled</span> :
                    <span className="badge badge-muted">Stopped</span>}
                </td>
                <td className="muted">{v.comment || '—'}</td>
                <td className="col-center col-action">
                  <button className="btn-icon" title={v.disabled ? 'Enable' : 'Disable'}
                    onClick={() => handleToggleVrrp(v.id, v.disabled)} disabled={!!savingAction}>
                    {savingAction === `toggle-${v.id}` ? <FaSpinner className="spin" /> :
                      v.disabled ? <FaToggleOff className="toggle-off" /> : <FaToggleOn className="toggle-on" />}
                  </button>
                </td>
                <td className="col-center col-action">
                  <button className="btn-icon btn-danger" title="Remove"
                    onClick={() => handleRemove(v.id, v.name)} disabled={!!savingAction}>
                    {savingAction === `rm-${v.id}` ? <FaSpinner className="spin" /> : <FaTrash />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// =====================================================================
// ArpPanel — ARP table viewer
// =====================================================================
const ArpPanel = ({ routerId, showError }) => {
  const apiCall = useApiCall();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    try {
      const data = await apiCall(`/api/RouterConfig/${routerId}/arp`);
      setEntries(data.data || []);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setLoading(false); }
  }, [routerId, apiCall, showError]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  const filtered = entries.filter(e => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return e.address?.toLowerCase().includes(f) ||
      e.macAddress?.toLowerCase().includes(f) ||
      e.interface?.toLowerCase().includes(f);
  });

  return (
    <div className="panel">
      <div className="panel-toolbar">
        <input className="search-input" placeholder="Filter ARP entries..."
          value={filter} onChange={e => setFilter(e.target.value)} />
        <div className="toolbar-actions">
          <span className="muted" style={{ fontSize: 12 }}>{filtered.length} entries</span>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      {loading && entries.length === 0 ? (
        <div className="empty">Loading ARP table...</div>
      ) : filtered.length === 0 ? (
        <div className="empty">{filter ? 'No ARP entries match filter' : 'No ARP entries found'}</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>IP Address</th><th>MAC Address</th><th>Interface</th>
              <th className="col-center" style={{ width: 80 }}>Flags</th><th>Comment</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.id} className={e.disabled ? 'row-disabled' : ''}>
                <td className="mono fw">{e.address}</td>
                <td className="mono">{e.macAddress || '—'}</td>
                <td>{e.interface}</td>
                <td className="col-center">
                  {e.dynamic && <span className="badge badge-info">D</span>}
                  {e.complete && <span className="badge badge-success">C</span>}
                  {e.disabled && <span className="badge badge-muted">X</span>}
                </td>
                <td className="muted">{e.comment || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// =====================================================================
// WifiPanel — Wireless (WiFi) management
// =====================================================================
/**
 * Friendly WiFi management for non-network-engineers. Shows each wireless
 * network (SSID, band, security, on/off status), lets the user rename the
 * SSID or turn a radio on/off through a guided modal with a clear warning,
 * and lists the devices currently connected to WiFi with their signal
 * quality translated into plain language.
 *
 * Degrades gracefully: routers without wireless hardware simply show a
 * friendly "no WiFi" message because the backend returns an empty list.
 */
const signalQuality = (raw) => {
  // RouterOS reports signal-strength like "-67@ht" or "-67". Lower (closer to 0)
  // is stronger. Translate dBm into a plain-language label + colour.
  if (!raw) return null;
  const m = String(raw).match(/-?\d+/);
  if (!m) return null;
  const dbm = parseInt(m[0], 10);
  if (dbm >= -60) return { label: 'Excellent', cls: 'wifi-sig--excellent', dbm };
  if (dbm >= -70) return { label: 'Good', cls: 'wifi-sig--good', dbm };
  if (dbm >= -80) return { label: 'Fair', cls: 'wifi-sig--fair', dbm };
  return { label: 'Weak', cls: 'wifi-sig--weak', dbm };
};

const WifiEditModal = ({ wifi, onClose, onSave, saving }) => {
  const [ssid, setSsid] = useState(wifi.ssid || '');
  const [enabled, setEnabled] = useState(!wifi.disabled);

  const ssidChanged = ssid.trim() !== (wifi.ssid || '');
  const enabledChanged = enabled === wifi.disabled; // disabled flips when changed
  const hasChange = ssidChanged || enabledChanged;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal__header">
          <h3><FaWifi /> Edit WiFi — {wifi.name}</h3>
          <button className="btn-icon" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="modal__body">
          <label className="form-label">Network name (SSID)</label>
          <input
            className="form-input"
            value={ssid}
            onChange={e => setSsid(e.target.value)}
            placeholder="e.g. CapitalLand-Office"
            maxLength={32}
          />
          <p className="form-hint">This is the WiFi name people see when they connect.</p>

          <label className="form-label" style={{ marginTop: 14 }}>Radio</label>
          <div className="wifi-toggle-row">
            <button
              className={`btn btn-sm ${enabled ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setEnabled(true)}
            >
              <FaToggleOn /> On
            </button>
            <button
              className={`btn btn-sm ${!enabled ? 'btn-danger' : 'btn-outline'}`}
              onClick={() => setEnabled(false)}
            >
              <FaToggleOff /> Off
            </button>
          </div>

          <div className="alert alert-warning" style={{ marginTop: 16 }}>
            <FaExclamationTriangle /> Renaming the network or turning the radio off
            will disconnect every device currently on this WiFi. They will need to
            reconnect (and re-enter the password if the name changed).
          </div>
        </div>
        <div className="modal__footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!hasChange || !ssid.trim() || saving}
            onClick={() => onSave({
              ssid: ssidChanged ? ssid.trim() : null,
              disabled: enabledChanged ? !enabled : null
            })}
          >
            {saving ? <><FaSpinner className="spin" /> Applying…</> : <><FaCheck /> Apply Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const WifiPanel = ({ routerId, showSuccess, showError }) => {
  const apiCall = useApiCall();
  const [interfaces, setInterfaces] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    try {
      const [ifaceData, regData] = await Promise.all([
        apiCall(`/api/RouterConfig/${routerId}/wireless`),
        apiCall(`/api/RouterConfig/${routerId}/wireless/registrations`)
      ]);
      setInterfaces(ifaceData.data || []);
      setRegistrations(regData.data || []);
    } catch (e) {
      Sentry.captureException(e);
      showError(e.message);
    } finally {
      setLoading(false);
    }
  }, [routerId, apiCall, showError]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  const handleSave = async (changes) => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await apiCall(`/api/RouterConfig/${routerId}/wireless/${encodeURIComponent(editTarget.id)}`, {
        method: 'PUT',
        body: JSON.stringify(changes)
      });
      showSuccess('WiFi network updated');
      setEditTarget(null);
      await load();
    } catch (e) {
      Sentry.captureException(e);
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Count connected devices per interface for a friendly summary
  const clientsByInterface = registrations.reduce((acc, r) => {
    acc[r.interface] = (acc[r.interface] || 0) + 1;
    return acc;
  }, {});

  const filteredRegs = registrations.filter(r => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      r.macAddress?.toLowerCase().includes(f) ||
      r.interface?.toLowerCase().includes(f) ||
      r.comment?.toLowerCase().includes(f)
    );
  });

  return (
    <div className="panel">
      <div className="panel-toolbar">
        <div>
          <strong style={{ fontSize: 14 }}><FaWifi /> WiFi Networks</strong>
          <div className="muted" style={{ fontSize: 12 }}>
            Manage the wireless networks this router broadcasts and see who is connected.
          </div>
        </div>
        <div className="toolbar-actions">
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      {loading && interfaces.length === 0 ? (
        <div className="empty">Loading WiFi configuration…</div>
      ) : interfaces.length === 0 ? (
        <div className="empty">
          <FaWifi style={{ fontSize: 24, opacity: 0.4, display: 'block', margin: '0 auto 8px' }} />
          This router has no WiFi radios — it is a wired-only device, or its wireless
          package is not installed. Nothing to configure here.
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Network (SSID)</th>
              <th>Interface</th>
              <th>Band</th>
              <th>Security</th>
              <th>Connected</th>
              <th>Status</th>
              <th className="col-center col-action" style={{ width: 60 }}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {interfaces.map(w => (
              <tr key={w.id} className={w.disabled ? 'row-disabled' : ''}>
                <td className="fw">
                  {w.ssid || <span className="muted">(no name set)</span>}
                </td>
                <td className="mono">{w.name}</td>
                <td>{w.band || <span className="muted">—</span>}</td>
                <td>
                  <span className="badge badge-type">
                    <FaLock style={{ marginRight: 3, fontSize: 10 }} />
                    {w.securityProfile || 'default'}
                  </span>
                </td>
                <td>
                  <span className="badge badge-info">
                    {clientsByInterface[w.name] || 0} device{(clientsByInterface[w.name] || 0) !== 1 ? 's' : ''}
                  </span>
                </td>
                <td>
                  {w.disabled ? (
                    <span className="badge badge-muted">Off</span>
                  ) : w.running ? (
                    <span className="badge badge-success">On & broadcasting</span>
                  ) : (
                    <span className="badge badge-warning">On (idle)</span>
                  )}
                </td>
                <td className="col-center col-action">
                  <button className="btn-icon" title="Edit WiFi network" onClick={() => setEditTarget(w)}>
                    <FaEdit />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Connected devices */}
      {interfaces.length > 0 && (
        <>
          <div className="panel-toolbar" style={{ marginTop: 20 }}>
            <strong style={{ fontSize: 14 }}><FaBroadcastTower /> Connected Devices</strong>
            <div className="toolbar-actions">
              <input
                className="search-input"
                placeholder="Filter by MAC, interface…"
                value={filter}
                onChange={e => setFilter(e.target.value)}
              />
              <span className="muted" style={{ fontSize: 12 }}>
                {filteredRegs.length} device{filteredRegs.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {filteredRegs.length === 0 ? (
            <div className="empty">
              {filter ? 'No connected devices match filter' : 'No devices are currently connected to WiFi.'}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>MAC Address</th>
                  <th>WiFi Network</th>
                  <th>Signal</th>
                  <th>Speed (Tx / Rx)</th>
                  <th>Connected For</th>
                </tr>
              </thead>
              <tbody>
                {filteredRegs.map((r, idx) => {
                  const sig = signalQuality(r.signalStrength);
                  return (
                    <tr key={r.id || idx}>
                      <td className="mono">{r.macAddress || '—'}</td>
                      <td className="mono">{r.interface}</td>
                      <td>
                        {sig ? (
                          <span className={`wifi-sig ${sig.cls}`} title={`${sig.dbm} dBm`}>
                            <FaSignal style={{ marginRight: 4, fontSize: 11 }} />
                            {sig.label}
                          </span>
                        ) : <span className="muted">—</span>}
                      </td>
                      <td className="mono">
                        {r.txRate || '—'} / {r.rxRate || '—'}
                      </td>
                      <td>{r.uptime || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      {editTarget && (
        <WifiEditModal
          wifi={editTarget}
          saving={saving}
          onClose={() => setEditTarget(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

// =====================================================================
// NeighboursPanel — IP Neighbours viewer (MNDP / CDP / LLDP)
// =====================================================================
/**
 * Displays directly attached devices discovered by the router via
 * MikroTik Neighbour Discovery Protocol (MNDP), CDP, or LLDP.
 * Data is read-only (discovery is passive), so only Refresh + Filter
 * are provided — no add / delete actions.
 */
const NeighboursPanel = ({ routerId, showError }) => {
  const apiCall = useApiCall();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    try {
      const data = await apiCall(`/api/RouterConfig/${routerId}/neighbours`);
      setEntries(data.data || []);
    }
    catch (e) {
        Sentry.captureException(e);
        showError(e.message);
    }
    finally { setLoading(false); }
  }, [routerId, apiCall, showError]);

  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  const filtered = entries.filter(e => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      e.identity?.toLowerCase().includes(f) ||
      e.address?.toLowerCase().includes(f) ||
      e.macAddress?.toLowerCase().includes(f) ||
      e.interface?.toLowerCase().includes(f) ||
      e.platform?.toLowerCase().includes(f) ||
      e.version?.toLowerCase().includes(f)
    );
  });

  return (
    <div className="panel">
      <div className="panel-toolbar">
        <input
          className="search-input"
          placeholder="Filter by identity, IP, MAC, interface, platform…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <div className="toolbar-actions">
          <span className="muted" style={{ fontSize: 12 }}>{filtered.length} neighbour{filtered.length !== 1 ? 's' : ''}</span>
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      {loading && entries.length === 0 ? (
        <div className="empty">Loading neighbours…</div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          {filter
            ? 'No neighbours match filter'
            : 'No neighbours discovered — ensure MNDP is enabled on this router'}
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Identity</th>
              <th>IP Address</th>
              <th>MAC Address</th>
              <th>Interface</th>
              <th>Platform</th>
              <th>Version</th>
              <th>Age</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((n, idx) => (
              <tr key={n.id || idx}>
                <td className="fw">{n.identity || <span className="muted">—</span>}</td>
                <td className="mono fw">
                  {n.address || <span className="muted">—</span>}
                  {n.address6 && (
                    <div className="muted" style={{ fontSize: 11 }}>{n.address6}</div>
                  )}
                </td>
                <td className="mono">{n.macAddress || <span className="muted">—</span>}</td>
                <td>
                  {n.interface || <span className="muted">—</span>}
                  {n.interfaceName && n.interfaceName !== n.interface && (
                    <div className="muted" style={{ fontSize: 11 }} title="Neighbour's local interface">
                      ↳ {n.interfaceName}
                    </div>
                  )}
                </td>
                <td>
                  {n.platform ? (
                    <span className="badge badge-info">{n.platform}</span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="muted" style={{ fontSize: 12 }}>{n.version || '—'}</td>
                <td className="muted" style={{ fontSize: 12 }}>{n.age || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// =====================================================================
// RouterInterfaceManagement — parent wrapper
// =====================================================================
const RouterInterfaceManagement = ({ routerId, routerConfig }) => {
  const [subTab, setSubTab] = useState('interfaces');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCli, setShowCli] = useState(false);

  const showSuccess = useCallback((msg) => { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }, []);
  const showError = useCallback((msg) => { setError(msg); setTimeout(() => setError(null), 8000); }, []);

  const subTabs = [
    { key: 'interfaces', label: 'Interfaces', icon: <FaEthernet /> },
    { key: 'addresses', label: 'IP Addresses', icon: <FaNetworkWired /> },
    { key: 'dhcp', label: 'DHCP', icon: <FaServer /> },
    { key: 'wifi', label: 'WiFi', icon: <FaWifi /> },
    { key: 'routes', label: 'Routes', icon: <FaRoute /> },
    { key: 'dns', label: 'DNS', icon: <FaDna /> },
    { key: 'vrrp', label: 'VRRP', icon: <FaExchangeAlt /> },
    { key: 'arp', label: 'ARP', icon: <FaTable /> },
    { key: 'neighbours', label: 'Neighbours', icon: <FaProjectDiagram /> },
    { key: 'log', label: 'Event Log', icon: <FaScroll /> },
    { key: 'audit', label: 'Audit Trail', icon: <FaHistory /> }
  ];

  return (
    <div className="router-interface-mgmt">
      {error && (
        <div className="alert alert-danger">
          <FaExclamationTriangle /> {error}
          <button className="btn-icon" onClick={() => setError(null)}><FaTimes /></button>
        </div>
      )}
      {success && (
        <div className="alert alert-success"><FaCheckCircle /> {success}</div>
      )}

      <div className="sub-tabs">
        {subTabs.map(t => (
          <button key={t.key}
            className={`sub-tab ${subTab === t.key ? 'active' : ''}`}
            onClick={() => setSubTab(t.key)}>
            {t.icon} {t.label}
          </button>
        ))}
        <div className="sub-tabs__spacer" />
        <button className="btn btn-dark btn-sm cli-open-btn" onClick={() => setShowCli(true)}>
          <FaTerminal /> Terminal
        </button>
      </div>

      {subTab === 'interfaces' && <InterfacesPanel routerId={routerId} showSuccess={showSuccess} showError={showError} />}
      {subTab === 'addresses' && <IpAddressesPanel routerId={routerId} showSuccess={showSuccess} showError={showError} />}
      {subTab === 'dhcp' && <DhcpPanel routerId={routerId} showSuccess={showSuccess} showError={showError} />}
      {subTab === 'wifi' && <WifiPanel routerId={routerId} showSuccess={showSuccess} showError={showError} />}
      {subTab === 'routes' && <RoutesPanel routerId={routerId} showSuccess={showSuccess} showError={showError} />}
      {subTab === 'dns' && <DnsPanel routerId={routerId} showSuccess={showSuccess} showError={showError} />}
      {subTab === 'vrrp' && <VrrpPanel routerId={routerId} showSuccess={showSuccess} showError={showError} />}
      {subTab === 'arp' && <ArpPanel routerId={routerId} showError={showError} />}
      {subTab === 'neighbours' && <NeighboursPanel routerId={routerId} showError={showError} />}
      {subTab === 'log' && <EventLogPanel routerId={routerId} showError={showError} />}
      {subTab === 'audit' && <AuditLogPanel routerId={routerId} showError={showError} />}

      {showCli && (
        <CliTerminalModal
          routerId={routerId}
          routerConfig={routerConfig}
          onClose={() => setShowCli(false)}
        />
      )}
    </div>
  );
};

export default RouterInterfaceManagement;
